// Video Reference Ads (Task #2504) — upload a reference video, pick a
// product, optionally type a variant direction (e.g. "LA nightlife"), and
// generate an original, reference-INSPIRED product ad: one Sim workflow
// analyzes the reference into a Seedance 2.5 prompt, then KIE Seedance 2.5
// renders it with the reference video + product image. Available to all
// brand users (no flag gating). Each submission is snapshotted and queued;
// prior active runs do not lock the next setup.
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams, useSearch } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Film, Package, Loader2, Check, AlertTriangle, RefreshCw, Upload,
  Sparkles, Video, X, FileJson, UserCircle, Trash2, Clapperboard,
  Bookmark, Clock, Music, Type, Pencil, Info, Heart, Play, Pause, Globe2,
  History, ChevronDown, ChevronUp, Eye, ListOrdered, Copy, Settings2,
  ExternalLink,
} from "lucide-react";
import type {
  Persona,
  PersonaCharacter,
  Product,
  Offer,
  VideoReferenceAdRun,
  VideoReferenceUpload,
} from "@shared/schema";
import { PickerSelect } from "@/components/picker-select";
import { VariantPicker } from "@/components/variant-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest, invalidateGeneratedAdListings } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { ensureGoogleFontsLoaded } from "@/lib/brand-font-loader";
import { selectablePersonas } from "@shared/persona-select";
import {
  clampVideoReferenceTextPositionToFrame,
  parseVideoReferenceVariantJsonWithSource,
  resolveVideoReferenceBatchVariants,
  VIDEO_REFERENCE_BATCH_MAX_ITEMS,
  VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS,
  VIDEO_REFERENCE_BATCH_UPLOADER_EXPLANATION,
  VIDEO_REFERENCE_BATCH_WORKED_EXAMPLE,
  getVideoReferenceDisplayedContentRect,
  videoReferenceCanvasPositionToPreview,
  videoReferenceCanvasTextMetrics,
  videoReferencePreviewPointToCanvasPosition,
  type VideoReferenceBatchVariant,
  type VideoReferenceQuickEditTimeline,
  type VideoReferenceTextPlacement,
  inferVideoReferenceTextPlacement,
} from "@shared/video-reference-batch";
import { normalizeProductVariants } from "@shared/product-variant";
import {
  normalizeVideoReferenceDisplayName,
  normalizeVideoReferenceSourceIdentity as normalizeReferenceSourceIdentity,
} from "@shared/video-reference-source";
import { GOOGLE_FONT_CATALOG } from "@shared/google-fonts";
import {
  VIDEO_REFERENCE_RECENT_GROUP_LIMIT,
  groupVideoReferenceRuns,
  type VideoReferenceRunViewGroup,
  type VideoReferenceRunsView,
} from "@shared/video-reference-run-view";
import {
  useVideoReferenceBrandPromptConfiguration,
  VideoReferenceBrandPromptDialog,
} from "@/components/video-reference-brand-prompt-dialog";
import type { VideoReferencePromptType } from "@/lib/video-reference-workflow-response";

export { normalizeReferenceSourceIdentity };
/** Minimal shape returned by GET /api/video-reference-assets */
interface VideoReferenceAssetPublic {
  id: string;
  kind: "font" | "music";
  label: string;
  fileName: string;
  format: string;
  fontFamily?: string | null;
  durationSec?: number | null;
  sizeBytes?: number | null;
  previewUrl: string;
}

const GOOGLE_FONT_OPTION_PREFIX = "__google-font__:";
interface VideoReferenceLibraryResponse {
  uploads: VideoReferenceUpload[];
  favoriteSourceKeys: string[];
  sourceDisplayNames?: Record<string, string>;
}

interface VideoReferenceTemplatePublic {
  id: string;
  kind?: string | null;
  name: string;
  referenceVideoUrl: string;
  referenceDurationSec: number;
  thumbnailUrl?: string | null;
}

const ACTIVE_STATUSES = new Set(["queued", "pending", "preparing_model", "analyzing", "rendering", "adding_text", "adding_music", "cropping"]);
const DELETABLE_STATUSES = new Set(["ready", "failed", "deleting"]);
const DEFAULT_EXACT_TEXT_FONT_SIZE = 55;
const DEFAULT_EXACT_TEXT_MAX_WIDTH_PERCENT = 100;
const KEEP_CURRENT_FONT = "__keep_current__";
const DEFAULT_FONT = "__default__";
type VideoReferenceTextAlign = "left" | "center" | "right";
const QUICK_EDIT_POSITION_BY_PLACEMENT: Record<VideoReferenceTextPlacement, number> = {
  top: 0.14,
  "upper-third": 1 / 3,
  middle: 0.5,
  "lower-third": 2 / 3,
  bottom: 0.86,
};

function resolveQuickEditTextPosition(
  position: { x: number; y: number },
  selectedPlacement: VideoReferenceTextPlacement,
  savedTextStyle: Record<string, unknown> | null | undefined,
  positionWasDragged: boolean,
): { x: number; y: number } {
  if (positionWasDragged) return position;
  const savedPlacement = savedTextStyle
    ? inferVideoReferenceTextPlacement(savedTextStyle as any)
    : "top";
  return selectedPlacement === savedPlacement
    ? position
    : { ...position, y: QUICK_EDIT_POSITION_BY_PLACEMENT[selectedPlacement] };
}

function formatTimelineTime(value: number): string {
  const safe = Math.max(0, Number.isFinite(value) ? value : 0);
  return `${Math.floor(safe / 60)}:${Math.floor(safe % 60).toString().padStart(2, "0")}`;
}

function quickEditErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error || "");
  if (/^404\b|not found|unavailable/i.test(message)) {
    return "This video is no longer available for quick editing. Refresh the Videos list and choose the newest successful output.";
  }
  if (/^401\b|^403\b|unauthorized|forbidden|access/i.test(message)) {
    return "You no longer have access to this brand or video. Refresh the page or switch back to an authorized brand.";
  }
  if (/^409\b|changed before|already being rendered|preserved clean source|soundtrack snapshot/i.test(message)) {
    return `${message.replace(/^\d+:\s*/, "")} Refresh the Videos list before trying again.`;
  }
  return message.replace(/^\d+:\s*/, "") || "Quick edit could not start. Refresh the Videos list and try again.";
}

type SanitizedRun = Omit<
  VideoReferenceAdRun,
  | "seedancePrompt"
  | "customModelPrompt"
  | "customModelSimUsageJson"
  | "customModelSimBilledAt"
  | "customModelKieTaskId"
  | "customModelKieSubmitStartedAt"
  | "customModelProviderImageUrl"
  | "customModelImageUrl"
  | "customModelProvider"
  | "customModelKieError"
  | "customModelKieBilledAt"
  | "customModelFalRequestId"
  | "customModelFalSubmitStartedAt"
  | "customModelFalError"
  | "customModelFalBilledAt"
  | "customModelSimClaimToken"
  | "customModelSimClaimedAt"
  | "customModelKieClaimToken"
  | "customModelKieClaimedAt"
  | "customModelFalClaimToken"
  | "customModelFalClaimedAt"
  | "videoRenderClaimToken"
  | "videoRenderClaimedAt"
  | "failureCategory"
  | "failureProviderCode"
  | "silentVideoUrl"
  | "textFontUrl"
  | "textFontSha256"
  | "textOverlayVideoUrl"
  | "musicPrompt"
  | "musicFalRequestId"
  | "musicProviderUrl"
  | "musicUrl"
  | "musicBilledAt"
  | "musicWebhookBaseUrl"
  | "musicClaimToken"
  | "musicClaimedAt"
  | "musicSubmitStartedAt"
  | "quickEditClaimToken"
  | "quickEditClaimedAt"
  | "quickEditPendingRecipeJson"
  | "derivedSourceVideoUrl"
  | "derivedClaimToken"
  | "derivedClaimedAt"
> & {
  isSaved?: boolean;
  textEditAvailable: boolean;
  textEditUnavailableReason: string | null;
  quickEditSourceUrl?: string | null;
  quickEditAvailable?: boolean;
  quickEditUnavailableReason?: string | null;
  quickEditInProgress?: boolean;
  quickEditTimeline?: VideoReferenceQuickEditTimeline | null;
  /** Sanitized provenance only; prompt wording is deliberately never returned. */
  videoPromptVersionNumber?: number | null;
  videoPromptProvenance?: {
    source: "in_app" | "sim";
    versionNumber: number | null;
  };
};

type VideoReferenceRunsData =
  | SanitizedRun[]
  | VideoReferenceRunsView<SanitizedRun>;
function runFontLabel(
  run: Pick<
    SanitizedRun,
    "textFontAssetLabel" | "textFontFamily" | "textStyleJson"
  >,
): string {
  const style = run.textStyleJson as Record<string, unknown> | null;
  return (
    run.textFontAssetLabel ||
    run.textFontFamily ||
    (typeof style?.fontFamily === "string" ? style.fontFamily : "") ||
    "Inter"
  );
}

interface UploadedReference {
  url: string;
  uploadId?: string;
  durationSec: number;
  width: number;
  height: number;
  fileName: string;
  kind?: string | null;
}

type StarterPromptNamedItem = { id: string; name: string };
export interface ReferenceLibraryItem extends UploadedReference {
  key: string;
  uploadId?: string;
  isFavorite: boolean;
  lastActivityAt: string | Date | null;
  lastUsedAt: string | Date | null;
  lastUploadedAt: string | Date | null;
}

export function referenceMatchesType(
  item: Pick<UploadedReference, "kind">,
  type: VideoReferencePromptType,
): boolean {
  return type === "general" || !item.kind || item.kind === type;
}

export function adaptVideoReferenceTemplates(
  templates: VideoReferenceTemplatePublic[],
): ReferenceLibraryItem[] {
  if (!Array.isArray(templates)) return [];
  return templates.flatMap((template) => {
    const url = typeof template.referenceVideoUrl === "string"
      ? template.referenceVideoUrl.trim()
      : "";
    const durationSec = Number(template.referenceDurationSec);
    if (!url || !Number.isFinite(durationSec) || durationSec <= 0) return [];
    return [{
      key: `template:${template.id}`,
      url,
      durationSec,
      width: 0,
      height: 0,
      fileName: normalizeVideoReferenceDisplayName(template.name) || fallbackReferenceName(url),
      kind: template.kind,
      isFavorite: false,
      lastActivityAt: null,
      lastUsedAt: null,
      lastUploadedAt: null,
    }];
  });
}

type VideoReferenceAdRequestInput = {
  productId: string;
  selectedVariantName: string | null;
  personaId: string;
  characterId: string;
  reference: UploadedReference;
};

function timestampMs(value: string | Date | null | undefined): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function fallbackReferenceName(url: string): string {
  try {
    const parsed = new URL(url);
    const name = decodeURIComponent(parsed.pathname.split("/").filter(Boolean).pop() || "");
    return name || "Reference video";
  } catch {
    return url.split(/[/?#]/).filter(Boolean).pop() || "Reference video";
  }
}

function hasUsableReferenceMetadata(candidate: {
  durationSec?: number | null;
  width?: number | null;
  height?: number | null;
  fileName?: string | null;
}): boolean {
  return (
    Number.isFinite(candidate.durationSec) &&
    Number(candidate.durationSec) > 0 &&
    (!!candidate.fileName || (Number(candidate.width) > 0 && Number(candidate.height) > 0))
  );
}

/**
 * Builds one stable source row from the saved upload library and already
 * loaded runs. Uploads supply the richer metadata; runs still make legacy or
 * failed-to-save references discoverable and contribute their latest-use time.
 */
export function deriveReferenceLibrary(
  uploads: VideoReferenceUpload[],
  runs: Array<Pick<SanitizedRun, "referenceVideoUrl" | "referenceDurationSec" | "createdAt">>,
  favoriteSourceKeys: Iterable<string> = [],
  sourceDisplayNames: Record<string, string> = {},
): ReferenceLibraryItem[] {
  type WorkingItem = ReferenceLibraryItem & {
    metadataAt: number;
    fallbackDurationAt: number;
  };
  const byKey = new Map<string, WorkingItem>();
  const favoriteKeys = new Set(
    [...favoriteSourceKeys].map(normalizeReferenceSourceIdentity).filter(Boolean),
  );

  const addCandidate = ({
    url,
    at,
    uploadId,
    fileName,
    durationSec,
    width,
    height,
    kind,
    isUpload,
  }: {
    url: string | null | undefined;
    at: string | Date | null | undefined;
    uploadId?: string;
    fileName?: string | null;
    durationSec?: number | null;
    width?: number | null;
    height?: number | null;
    kind?: string | null;
    isUpload: boolean;
  }) => {
    if (!url?.trim()) return;
    const key = normalizeReferenceSourceIdentity(url);
    if (!key) return;
    const atMs = timestampMs(at);
    const current = byKey.get(key);
    const candidateHasMetadata = hasUsableReferenceMetadata({ fileName, durationSec, width, height });
    const overrideName = normalizeVideoReferenceDisplayName(sourceDisplayNames[key]);
    if (!current) {
      byKey.set(key, {
        key,
        url: url.trim(),
        uploadId,
        isFavorite: favoriteKeys.has(key),
        durationSec: Number(durationSec) > 0 ? Number(durationSec) : 0,
        width: Number(width) > 0 ? Number(width) : 0,
        height: Number(height) > 0 ? Number(height) : 0,
        kind: kind || null,
        fileName: overrideName || fileName?.trim() || fallbackReferenceName(url),
        lastActivityAt: at || null,
        lastUsedAt: isUpload ? null : at || null,
        lastUploadedAt: isUpload ? at || null : null,
        metadataAt: candidateHasMetadata ? atMs : 0,
        fallbackDurationAt: Number(durationSec) > 0 ? atMs : 0,
      });
      return;
    }

    if (atMs > timestampMs(current.lastActivityAt)) current.lastActivityAt = at || current.lastActivityAt;
    if (!isUpload && atMs > timestampMs(current.lastUsedAt)) current.lastUsedAt = at || current.lastUsedAt;
    if (isUpload && atMs > timestampMs(current.lastUploadedAt)) {
      current.lastUploadedAt = at || current.lastUploadedAt;
      current.uploadId = uploadId || current.uploadId;
    }
    if (isUpload && kind) current.kind = kind;

    if (candidateHasMetadata && atMs >= current.metadataAt) {
      current.metadataAt = atMs;
      current.fileName = overrideName || fileName?.trim() || current.fileName || fallbackReferenceName(url);
      if (Number(durationSec) > 0) current.durationSec = Number(durationSec);
      if (Number(width) > 0) current.width = Number(width);
      if (Number(height) > 0) current.height = Number(height);
      if (uploadId) current.uploadId = uploadId;
    } else if (Number(durationSec) > 0 && atMs >= current.fallbackDurationAt && !current.durationSec) {
      current.durationSec = Number(durationSec);
      current.fallbackDurationAt = atMs;
    }
  };

  uploads.forEach((upload) => addCandidate({
    url: upload.url,
    at: upload.createdAt,
    uploadId: upload.id,
    fileName: upload.fileName,
    durationSec: upload.durationSec,
    width: upload.width,
    height: upload.height,
    kind: upload.kind,
    isUpload: true,
  }));
  runs.forEach((run) => addCandidate({
    url: run.referenceVideoUrl,
    at: run.createdAt,
    durationSec: run.referenceDurationSec,
    isUpload: false,
  }));

  return [...byKey.values()]
    .sort((a, b) => timestampMs(b.lastActivityAt) - timestampMs(a.lastActivityAt))
    .map(({ metadataAt: _metadataAt, fallbackDurationAt: _fallbackDurationAt, ...item }) => item);
}

function referenceFromLibraryItem(item: ReferenceLibraryItem): UploadedReference {
  return {
    url: item.url,
    uploadId: item.uploadId,
    durationSec: item.durationSec,
    width: item.width,
    height: item.height,
    fileName: item.fileName,
    kind: item.kind,
  };
}

// Legacy request fields stay unchanged when the type-split grant is absent.
function buildVideoReferenceAdRequestBase({
  productId,
  selectedVariantName,
  personaId,
  characterId,
  reference,
}: VideoReferenceAdRequestInput) {
  return {
    productId,
    selectedVariantName,
    personaId,
    characterId,
    referenceVideoUrl: reference.url,
    referenceDurationSec: reference.durationSec,
  };
}

function statusMeta(run: SanitizedRun): { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Video; spinning: boolean } {
  switch (run.status) {
    case "pending":
      return { label: "Starting…", variant: "secondary", icon: Loader2, spinning: true };
    case "queued":
      return { label: "Queued", variant: "outline", icon: Loader2, spinning: false };
    case "preparing_model":
      return { label: "Preparing custom model", variant: "secondary", icon: Sparkles, spinning: true };
    case "analyzing":
      return { label: "Analyzing reference video", variant: "secondary", icon: Sparkles, spinning: true };
    case "rendering":
      return { label: "Generating video", variant: "secondary", icon: Loader2, spinning: true };
    case "adding_text":
      return { label: "Adding exact text", variant: "secondary", icon: Loader2, spinning: true };
    case "adding_music":
      return { label: "Adding background music", variant: "secondary", icon: Loader2, spinning: true };
    case "cropping":
      return { label: "Cropping to 4:5", variant: "secondary", icon: Loader2, spinning: true };
    case "ready":
      return { label: "Ready", variant: "default", icon: Check, spinning: false };
    case "failed":
      return { label: "Failed", variant: "destructive", icon: AlertTriangle, spinning: false };
    case "deleting":
      return { label: "Delete pending", variant: "outline", icon: Trash2, spinning: false };
    default:
      return { label: run.status, variant: "outline", icon: Video, spinning: false };
  }
}

function runGroupTitle(group: VideoReferenceRunViewGroup<SanitizedRun>): string {
  if (group.kind === "batch") return `Batch of ${group.runs.length}`;
  const run = group.runs[0];
  return run?.productName || "Video run";
}
function formatWhen(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function RunSnapshotThumbnail({
  url,
  kind,
}: {
  url: string | null | undefined;
  kind: "product" | "model";
}) {
  const [failed, setFailed] = useState(false);
  const fallback =
    kind === "product"
      ? <Package className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      : <UserCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />;
  if (!url || failed) {
    return <span className="flex h-5 w-5 shrink-0 items-center justify-center">{fallback}</span>;
  }
  return (
    <img
      src={url}
      alt=""
      className={`h-5 w-5 shrink-0 object-cover ${kind === "product" ? "rounded-sm" : "rounded-full"}`}
      onError={() => setFailed(true)}
    />
  );
}

function ReferenceLibraryCard({
  item,
  index,
  context,
  isSelected,
  selectionDisabled,
  favoritePending,
  onSelect,
  onFavorite,
  onRename,
  onPhonePreview,
  renamePending,
}: {
  item: ReferenceLibraryItem;
  index: number;
  context: "recent" | "favorite" | "library" | "template";
  isSelected: boolean;
  selectionDisabled: boolean;
  favoritePending: boolean;
  onSelect: (item: ReferenceLibraryItem) => void;
  onFavorite: (item: ReferenceLibraryItem) => void;
  onRename: (item: ReferenceLibraryItem, displayName: string) => Promise<void>;
  onPhonePreview: (item: ReferenceLibraryItem) => void;
  renamePending: boolean;
}) {
  const compact = context === "library";
  const denseFavorite = context === "favorite";
  const isTemplate = context === "template";
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(item.fileName);
  const [renameError, setRenameError] = useState<string | null>(null);

  useEffect(() => {
    if (!renaming) setDraftName(item.fileName);
  }, [item.fileName, renaming]);

  const startRename = () => {
    setDraftName(item.fileName);
    setRenameError(null);
    setRenaming(true);
  };
  const cancelRename = () => {
    setDraftName(item.fileName);
    setRenameError(null);
    setRenaming(false);
  };
  const saveRename = async () => {
    if (renamePending) return;
    const displayName = normalizeVideoReferenceDisplayName(draftName);
    if (!displayName) {
      setRenameError("Enter a name with 1–255 characters.");
      return;
    }
    setRenameError(null);
    try {
      await onRename(item, displayName);
      setRenaming(false);
    } catch (error: any) {
      setRenameError(error?.message || "Could not rename reference.");
    }
  };

  return (
    <div
      className={[
        "group min-w-0 overflow-hidden rounded-xl border bg-background transition-all",
        compact || denseFavorite ? "flex items-start gap-2 p-2" : "",
        isSelected
          ? "border-primary ring-2 ring-primary/30 shadow-sm"
          : "border-border/60 hover:border-primary/40 hover:shadow-md",
      ].join(" ")}
      data-testid={`${context}-reference-card-${index}`}
    >
      <div
        className={[
          "relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-lg bg-black",
          compact ? "w-20 shrink-0" : denseFavorite ? "w-32 shrink-0 sm:w-40" : "",
        ].join(" ")}
        data-testid={`${context}-reference-preview-frame-${index}`}
      >
        <video
          src={item.url}
          className="absolute inset-0 h-full w-full bg-black object-contain"
          controls
          playsInline
          preload="metadata"
          aria-label={`Preview ${item.fileName}`}
          data-testid={`${context}-reference-video-${index}`}
        />
        <div
          className="absolute right-3 top-3 z-10"
          data-testid={`phone-preview-position-${context}-reference-${index}`}
        >
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="aspect-square shrink-0 rounded-full border border-white/15 bg-neutral-900/80 p-0 text-white shadow-lg backdrop-blur-sm hover:scale-105 hover:bg-neutral-900 hover:text-white"
            style={{ width: 44, height: 44, minWidth: 44 }}
            aria-label={`Open ${item.fileName} in phone preview`}
            title="Open phone preview"
            onClick={(event) => {
              event.stopPropagation();
              onPhonePreview(item);
            }}
            data-testid={`button-phone-preview-${context}-reference-${index}`}
          >
            <ExternalLink className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
        {!compact && (
          <span className="pointer-events-none absolute bottom-2 right-2 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {Math.round(item.durationSec)}s
          </span>
        )}
        {isSelected && !compact && (
          <span className="pointer-events-none absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground shadow" data-testid={`badge-selected-${context}-reference-${index}`}>
            <Check className="h-3 w-3" aria-hidden="true" /> Selected
          </span>
        )}
      </div>
      <div className={compact || denseFavorite ? "flex min-w-0 flex-1 flex-col gap-2 py-0.5" : "min-w-0 space-y-2 p-2"}>
        <div className="min-w-0">
          {renaming ? (
            <div className="space-y-1.5">
              <Input
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (!renamePending) void saveRename();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    cancelRename();
                  }
                }}
                maxLength={255}
                autoFocus
                disabled={renamePending}
                aria-label={`New name for ${item.fileName}`}
                data-testid={`input-rename-${context}-reference-${index}`}
              />
              <div className="flex flex-wrap items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveRename()}
                  disabled={renamePending}
                  aria-label={`Save name for ${item.fileName}`}
                  data-testid={`button-save-rename-${context}-reference-${index}`}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={cancelRename}
                  disabled={renamePending}
                  data-testid={`button-cancel-rename-${context}-reference-${index}`}
                >
                  Cancel
                </Button>
              </div>
              {renameError && <p className="text-[11px] text-destructive" role="alert">{renameError}</p>}
            </div>
          ) : (
            <>
              <div className="flex min-w-0 items-center gap-1">
                <p className="min-w-0 flex-1 truncate text-xs font-medium" title={item.fileName}>{item.fileName}</p>
                {!isTemplate && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
                    onClick={startRename}
                    disabled={renamePending}
                    aria-label={`Rename ${item.fileName}`}
                    data-testid={`button-rename-${context}-reference-${index}`}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
              <p className="truncate text-[11px] text-muted-foreground" data-testid={`${context}-reference-metadata-${index}`}>
                {compact ? `${Math.round(item.durationSec)}s • ` : ""}
                {isTemplate ? "Curated template" : item.lastUsedAt ? `Last used ${formatWhen(item.lastUsedAt)}` : item.lastUploadedAt ? `Added ${formatWhen(item.lastUploadedAt)}` : "Saved reference"}
              </p>
            </>
          )}
        </div>
        {!renaming && <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={isSelected ? "secondary" : compact || denseFavorite ? "outline" : "ghost"}
            className={compact || denseFavorite ? "" : "h-7 flex-1 justify-start px-2 text-xs"}
            onClick={() => onSelect(item)}
            disabled={selectionDisabled}
            aria-pressed={isSelected}
            aria-label={`Use ${item.fileName}`}
            data-testid={`button-use-${context}-reference-${index}`}
          >
            {isSelected ? "Selected" : compact || denseFavorite ? "Use this reference" : "Select"}
          </Button>
          {!isTemplate && (
            <>
              <Button
                type="button"
                size="icon"
                variant={item.isFavorite ? "secondary" : "ghost"}
                className="h-8 w-8"
                onClick={() => onFavorite(item)}
                disabled={favoritePending}
                aria-label={item.isFavorite ? `Remove ${item.fileName} from favorites` : `Add ${item.fileName} to favorites`}
                data-testid={`button-favorite-${context}-reference-${index}`}
              >
                <Heart className={`h-4 w-4 ${item.isFavorite ? "fill-current" : ""}`} aria-hidden="true" />
              </Button>
              {item.isFavorite && <Badge variant="secondary" className="text-[10px]">Favorite</Badge>}
            </>
          )}
        </div>}
      </div>
    </div>
  );
}

function FontUrlImportForm({
  disabled,
  testIdSuffix = "",
  onImport,
  onImportGoogle,
}: {
  disabled?: boolean;
  testIdSuffix?: string;
  onImport: (label: string, fontUrl: string) => Promise<string>;
  onImportGoogle: (family: string) => Promise<string>;
}) {
  const [mode, setMode] = useState<"url" | "google">("url");
  const [label, setLabel] = useState("");
  const [fontUrl, setFontUrl] = useState("");
  const [googleFamily, setGoogleFamily] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isImporting || disabled) return;
    if (mode === "google") {
      if (!googleFamily) {
        setFeedback({ type: "error", text: "Choose a Google Font." });
        return;
      }
      setFeedback(null);
      setIsImporting(true);
      try {
        const message = await onImportGoogle(googleFamily);
        setFeedback({ type: "success", text: message });
        setGoogleFamily("");
      } catch (error: any) {
        setFeedback({
          type: "error",
          text: error?.message || "Google Font import failed.",
        });
      } finally {
        setIsImporting(false);
      }
      return;
    }
    const nextLabel = label.trim();
    const nextUrl = fontUrl.trim();
    if (!nextLabel) {
      setFeedback({ type: "error", text: "Enter a display label." });
      return;
    }
    if (!nextUrl) {
      setFeedback({ type: "error", text: "Enter a direct public HTTPS font URL." });
      return;
    }
    setFeedback(null);
    setIsImporting(true);
    try {
      const message = await onImport(nextLabel, nextUrl);
      setFeedback({ type: "success", text: message });
      setLabel("");
      setFontUrl("");
    } catch (error: any) {
      setFeedback({
        type: "error",
        text: error?.message || "Font import failed.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <form
      className="space-y-3 rounded-md border border-dashed border-border/60 bg-muted/20 p-3"
      onSubmit={submit}
      data-testid={`form-import-font-url${suffix}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={mode === "url" ? "secondary" : "ghost"}
          className="h-7 px-2 text-xs"
          onClick={() => { setMode("url"); setFeedback(null); }}
          disabled={disabled || isImporting}
          data-testid={`button-font-import-mode-url${suffix}`}
        >
          <Globe2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Import from HTTPS
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "google" ? "secondary" : "ghost"}
          className="h-7 px-2 text-xs"
          onClick={() => { setMode("google"); setFeedback(null); }}
          disabled={disabled || isImporting}
          data-testid={`button-font-import-mode-google${suffix}`}
        >
          <Type className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Choose from Google Fonts
        </Button>
      </div>
      {mode === "google" ? (
        <>
          <p className="text-xs text-muted-foreground">
            Choose a curated family. Its validated TTF is copied into this catalog,
            so renders never depend on Google at runtime.
          </p>
          <select
            value={googleFamily}
            onChange={(event) => {
              setGoogleFamily(event.target.value);
              setFeedback(null);
            }}
            disabled={disabled || isImporting}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            aria-label={`Google Font family${testIdSuffix ? ` (${testIdSuffix})` : ""}`}
            data-testid={`select-import-google-font${suffix}`}
          >
            <option value="">Choose a family…</option>
            {GOOGLE_FONT_CATALOG.map((font) => (
              <option key={font.family} value={font.family}>
                {font.family} · {font.category}
              </option>
            ))}
          </select>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Enter a display label and a direct, publicly reachable HTTPS URL ending in{" "}
            <code>.ttf</code> or <code>.otf</code>. Credentials, private-network or unsafe
            redirect targets, oversized files, mismatched formats, and unreadable fonts are rejected.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                setFeedback(null);
              }}
              placeholder="Display label"
              aria-label={`Font display label${testIdSuffix ? ` (${testIdSuffix})` : ""}`}
              maxLength={120}
              disabled={disabled || isImporting}
              data-testid={`input-import-font-label${suffix}`}
            />
            <Input
              type="url"
              value={fontUrl}
              onChange={(event) => {
                setFontUrl(event.target.value);
                setFeedback(null);
              }}
              placeholder="https://cdn.example.com/font.ttf"
              aria-label={`Direct HTTPS font URL${testIdSuffix ? ` (${testIdSuffix})` : ""}`}
              maxLength={2048}
              disabled={disabled || isImporting}
              data-testid={`input-import-font-url${suffix}`}
            />
          </div>
        </>
      )}
      {feedback ? (
        <p
          className={feedback.type === "error"
            ? "text-xs text-destructive"
            : "text-xs text-green-600 dark:text-green-400"}
          role={feedback.type === "error" ? "alert" : "status"}
          data-testid={`text-import-font-${feedback.type}${suffix}`}
        >
          {feedback.text}
        </p>
      ) : null}
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={disabled || isImporting || (mode === "google"
          ? !googleFamily
          : !label.trim() || !fontUrl.trim())}
        data-testid={mode === "google"
          ? `button-submit-import-google-font${suffix}`
          : `button-submit-import-font-url${suffix}`}
      >
        {isImporting ? (
          <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Importing…</>
        ) : (
          mode === "google"
            ? <><Type className="mr-1 h-3.5 w-3.5" /> Add Google Font</>
            : <><Globe2 className="mr-1 h-3.5 w-3.5" /> Import font</>
        )}
      </Button>
    </form>
  );
}

export default function VideoReferenceAdsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const variantsInputRef = useRef<HTMLInputElement>(null);
  const musicAssetInputRef = useRef<HTMLInputElement>(null);
  const quickMusicAssetInputRef = useRef<HTMLInputElement>(null);
  const modelReferenceInputRef = useRef<HTMLInputElement>(null);
  const pendingAssetSelectionRef = useRef<string | null>(null);

  const [reference, setReference] = useState<UploadedReference | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer" | "none">("product");
  const [promptType, setPromptType] = useState<VideoReferencePromptType>("general");
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [variant, setVariant] = useState("");
  const [exactText, setExactText] = useState("");
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>("");
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>("");
  const [modelReferenceOverrideUrl, setModelReferenceOverrideUrl] = useState("");
  const [uploadingModelReference, setUploadingModelReference] = useState(false);
  const [fontAssetId, setFontAssetId] = useState<string>("");
  const [pendingGoogleFont, setPendingGoogleFont] = useState<{
    target: "new-run" | "text-edit";
    family: string;
  } | null>(null);
  const [textPlacement, setTextPlacement] =
    useState<VideoReferenceTextPlacement>("top");
  const [musicAssetId, setMusicAssetId] = useState<string>("__auto__");
  const [uploadingAssetKind, setUploadingAssetKind] =
    useState<"music" | null>(null);
  const [batchVariants, setBatchVariants] = useState<VideoReferenceBatchVariant[]>([]);
  const [batchSource, setBatchSource] = useState<unknown>(null);
  const [batchFileName, setBatchFileName] = useState("");
  const [batchRequestId, setBatchRequestId] = useState("");
  const [batchFileLoading, setBatchFileLoading] = useState(false);
  // Keep batch uploads available by direct link without exposing a mode picker
  // in the guided creation form.
  const search = useSearch();
  const [location, setLocation] = useLocation();
  const configurationMode = new URLSearchParams(search).get("mode") === "json" ? "json" : "manual";
  const [phonePreview, setPhonePreview] = useState<{
    videoUrl: string;
    displayName: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SanitizedRun | null>(null);
  const [textEditTarget, setTextEditTarget] = useState<SanitizedRun | null>(null);
  const [textEditValue, setTextEditValue] = useState("");
  const [textEditPlacement, setTextEditPlacement] =
    useState<VideoReferenceTextPlacement>("top");
  const [textEditFontSize, setTextEditFontSize] = useState(DEFAULT_EXACT_TEXT_FONT_SIZE);
  const [textEditMaxWidthPercent, setTextEditMaxWidthPercent] =
    useState(DEFAULT_EXACT_TEXT_MAX_WIDTH_PERCENT);
  const [textEditFontChoice, setTextEditFontChoice] = useState(KEEP_CURRENT_FONT);
  const [textEditPosition, setTextEditPosition] = useState({ x: 0.5, y: 0.14 });
  const [textEditPositionWasDragged, setTextEditPositionWasDragged] = useState(false);
  const [quickTrimStart, setQuickTrimStart] = useState(0);
  const [quickTrimEnd, setQuickTrimEnd] = useState(0);
  // The visible preview is the latest finished output. This offset translates
  // its local trim handles back onto the immutable clean-source timeline.
  const [quickEditSourceOffset, setQuickEditSourceOffset] = useState(0);
  const [quickAudio, setQuickAudio] = useState<"keep" | "mute" | "replace">("keep");
  const [quickMusicAssetId, setQuickMusicAssetId] = useState("");
  const [quickTextStart, setQuickTextStart] = useState(0);
  const [quickTextEnd, setQuickTextEnd] = useState(0);
  const [quickPlayhead, setQuickPlayhead] = useState(0);
  const [quickTextColor, setQuickTextColor] = useState("#FFFFFF");
  const [quickTextWeight, setQuickTextWeight] = useState(700);
  const [quickTextAlign, setQuickTextAlign] = useState<VideoReferenceTextAlign>("center");
  const [quickIsPlaying, setQuickIsPlaying] = useState(false);
  const [timelineTrimDrag, setTimelineTrimDrag] = useState<"start" | "end" | null>(null);
  const [selectedEditorLayer, setSelectedEditorLayer] =
    useState<"video" | "text" | "audio">("text");
  const quickEditVideoRef = useRef<HTMLVideoElement>(null);
  const quickEditTextRef = useRef<HTMLButtonElement>(null);
  const quickPreviewContainerRef = useRef<HTMLDivElement>(null);
  const [quickPreviewContentRect, setQuickPreviewContentRect] =
    useState<ReturnType<typeof getVideoReferenceDisplayedContentRect>>(null);
  const textEditInputRef = useRef<HTMLTextAreaElement>(null);
  const timelineVideoLaneRef = useRef<HTMLDivElement>(null);
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [savedRunIds, setSavedRunIds] = useState<Set<string>>(() => new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [cropFocusRunId, setCropFocusRunId] = useState<string | null>(null);
  const cropFocusStatusRef = useRef<string | null>(null);
  const [historyResultsTarget, setHistoryResultsTarget] =
    useState<HTMLDivElement | null>(null);
  const [highlightedRunIds, setHighlightedRunIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [promptConfigurationOpen, setPromptConfigurationOpen] = useState(false);
  const [promptConfigurationType, setPromptConfigurationType] = useState<VideoReferencePromptType>("general");
  const hasExactText = exactText.length > 0;

  const clampQuickEditTextPosition = (position: { x: number; y: number }) => {
    if (!quickPreviewContentRect) return position;
    const textRect = quickEditTextRef.current?.getBoundingClientRect();
    const blockWidth = textRect?.width
      ? textRect.width / quickPreviewContentRect.width
      : textEditMaxWidthPercent / 100;
    const blockHeight = textRect?.height
      ? textRect.height / quickPreviewContentRect.height
      : 0;
    const clamped = clampVideoReferenceTextPositionToFrame({
      position,
      anchor: "middle-center",
      blockWidth,
      blockHeight,
    });
    return clamped.position;
  };

  const updateQuickPreviewGeometry = () => {
    const container = quickPreviewContainerRef.current;
    const video = quickEditVideoRef.current;
    if (!container || !video) {
      setQuickPreviewContentRect(null);
      return;
    }
    const bounds = container.getBoundingClientRect();
    setQuickPreviewContentRect(
      getVideoReferenceDisplayedContentRect(
        bounds.width,
        bounds.height,
        video.videoWidth,
        video.videoHeight,
      ),
    );
  };

  useEffect(() => {
    if (!textEditTarget) {
      setQuickPreviewContentRect(null);
      return;
    }
    const update = () => updateQuickPreviewGeometry();
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (quickPreviewContainerRef.current) {
      resizeObserver?.observe(quickPreviewContainerRef.current);
    }
    window.addEventListener("resize", update);
    update();
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [textEditTarget?.id, textEditTarget?.quickEditSourceUrl]);

  const { data: productsData, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const products = Array.isArray(productsData) ? productsData : [];
  const selectedProduct = products.find((product) => product.id === selectedProductId);
  const { data: personasData, isLoading: personasLoading } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const personas = Array.isArray(personasData) ? personasData : [];
  const { data: personaCharactersData, isLoading: charactersLoading } = useQuery<PersonaCharacter[]>({
    queryKey: [`/api/personas/${selectedPersonaId}/characters`],
    enabled: !!selectedPersonaId,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      const characters = (query.state.data as PersonaCharacter[] | undefined) || [];
      return characters.some(
        (character) => character.avatarStatus === "pending" || character.avatarStatus === "generating",
      )
        ? 3000
        : false;
    },
  });
  const personaCharacters = Array.isArray(personaCharactersData) ? personaCharactersData : [];

  const runsKey = `/api/brands/${brandId}/video-reference-ads/runs`;
  const uploadsKey = `/api/brands/${brandId}/video-reference-ads/uploads`;
  const referenceLibraryKey = `/api/brands/${brandId}/video-reference-ads/reference-library`;
  const templatesKey = `/api/brands/${brandId}/video-reference-ads/templates`;
  const assetsKey = `/api/brands/${brandId}/video-reference-assets`;
  const { data: promptConfiguration } = useVideoReferenceBrandPromptConfiguration(brandId);
  const canConfigurePrompts = user?.isMaster === true;
  const { data: videoReferenceAvailability } = useQuery<{ enabled: boolean }>({
    queryKey: [`/api/brands/${brandId}/video-reference-ads/availability`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/brands/${brandId}/video-reference-ads/availability`);
      return response.json();
    },
    // The tool itself is master-only (API prefix and direct-route guard).
    // Do not widen this query to non-masters just to reveal type controls.
    enabled: !!brandId && canConfigurePrompts,
    retry: false,
  });
  const typeSplitEnabled = canConfigurePrompts && videoReferenceAvailability?.enabled === true;
  const { data: offersData, isLoading: offersLoading } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId && typeSplitEnabled,
  });
  const offers = Array.isArray(offersData) ? offersData : [];

  const {
    data: referenceAssetsData,
    isLoading: referenceAssetsLoading,
    isFetching: referenceAssetsFetching,
    error: referenceAssetsError,
  } = useQuery<VideoReferenceAssetPublic[]>({
    queryKey: [assetsKey],
    queryFn: async () => {
      const res = await fetch(assetsKey, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Could not load the font library");
      }
      return res.json();
    },
    enabled: !!brandId,
    staleTime: 60_000,
  });
  const referenceAssets = Array.isArray(referenceAssetsData) ? referenceAssetsData : [];

  const fontAssets = useMemo(
    () => referenceAssets.filter((a) => a.kind === "font"),
    [referenceAssets],
  );
  const musicAssets = useMemo(
    () => referenceAssets.filter((a) => a.kind === "music"),
    [referenceAssets],
  );
  const selectedFontAsset = fontAssets.find((asset) => asset.id === fontAssetId);
  const selectedTextEditFontAsset = fontAssets.find(
    (asset) => asset.id === textEditFontChoice,
  );
  const currentTextEditFontAsset =
    textEditFontChoice === KEEP_CURRENT_FONT
      ? fontAssets.find((asset) => asset.id === textEditTarget?.textFontAssetId) ||
        (textEditTarget?.textFontAssetId && brandId
          ? {
              id: textEditTarget.textFontAssetId,
              kind: "font" as const,
              label: runFontLabel(textEditTarget),
              fileName: "",
              format: "",
              fontFamily: textEditTarget.textFontFamily,
              previewUrl: `/api/brands/${brandId}/video-reference-assets/${textEditTarget.textFontAssetId}/content`,
            }
          : undefined)
      : undefined;
  const textEditSpecimenAsset =
    selectedTextEditFontAsset || currentTextEditFontAsset;
  const selectedMusicAsset = musicAssets.find((asset) => asset.id === musicAssetId);
  const selectedQuickMusicAsset = musicAssets.find((asset) => asset.id === quickMusicAssetId);
  // JSON is a complete, independent configuration. It must never inherit
  // selections that happen to be hidden in the Manual mode.
  const batchDefaults = useMemo(() => ({}), []);
  const batchResolution = useMemo(() => {
    if (batchSource === null) return { resolution: null, error: null };
    try {
      const resolution = resolveVideoReferenceBatchVariants(
        batchSource,
        batchDefaults,
        { allowRoleOnly: typeSplitEnabled },
      );
      for (const [index, item] of resolution.items.entries()) {
        const missing = [
          !typeSplitEnabled && !item.personaId && "personaId",
          !typeSplitEnabled && !item.characterId && "characterId",
          (typeSplitEnabled
            ? (item.promotedItemType === "offer" ? !item.offerId : item.promotedItemType !== "none" && !item.productId)
            : !item.productId) && (item.promotedItemType === "offer" && typeSplitEnabled ? "offerId" : "productId"),
          !item.referenceUploadId && "referenceUploadId",
        ].filter(Boolean);
        if (missing.length) {
          throw new Error(`Item ${index + 1} is missing ${missing.join(", ")}`);
        }
        if (typeSplitEnabled && !!item.personaId !== !!item.characterId) {
          throw new Error(`Item ${index + 1} needs both personaId and characterId, or neither`);
        }
      }
      return {
        resolution,
        error: null,
      };
    } catch (error: any) {
      return { resolution: null, error: error?.message || "Could not resolve batch defaults" };
    }
  }, [batchDefaults, batchSource, typeSplitEnabled]);
  const resolvedBatchVariants = batchResolution.resolution?.items ?? batchVariants;
  const resolvedBatchSources = batchResolution.resolution?.sources ?? [];

  // Every upload is saved to a brand-scoped library. The current user's
  // favorite keys travel with this response; runs are included below so legacy
  // references and uploads whose library write failed remain usable.
  const { data: referenceLibraryData, isLoading: uploadsLoading } = useQuery<VideoReferenceLibraryResponse>({
    queryKey: [referenceLibraryKey],
    queryFn: async () => {
      const res = await fetch(referenceLibraryKey, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Could not load reference library");
      }
      return res.json();
    },
    enabled: !!brandId,
  });
  const pastUploads = Array.isArray(referenceLibraryData?.uploads)
    ? referenceLibraryData.uploads
    : [];
  const favoriteSourceKeys = Array.isArray(referenceLibraryData?.favoriteSourceKeys)
    ? referenceLibraryData.favoriteSourceKeys
    : [];
  const sourceDisplayNames = referenceLibraryData?.sourceDisplayNames || {};
  const {
    data: referenceTemplatesData,
    isLoading: templatesLoading,
    error: templatesError,
  } = useQuery<VideoReferenceTemplatePublic[]>({
    queryKey: [templatesKey],
    queryFn: async () => {
      const res = await fetch(templatesKey, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Could not load reference templates");
      }
      return res.json();
    },
    enabled: !!brandId,
  });
  const { data: runsData, isLoading: runsLoading } = useQuery<VideoReferenceRunsData>({
    queryKey: [runsKey],
    enabled: !!brandId,
    refetchInterval: (query) => {
      const data = query.state.data as VideoReferenceRunsData | undefined;
      const rows = Array.isArray(data)
        ? data
        : [...(data?.active || []), ...(data?.recent || [])].flatMap((group) => group.runs);
      return rows.some(isRunViewActive) ? 4000 : false;
    },
  });
  const legacyRunsResponse = Array.isArray(runsData);
  const groupedRuns = useMemo<VideoReferenceRunsView<SanitizedRun>>(
    () => Array.isArray(runsData)
      ? groupVideoReferenceRuns(runsData, isRunViewActive)
      : runsData || { active: [], recent: [] },
    [runsData],
  );
  const runs = useMemo(
    () => [...groupedRuns.active, ...groupedRuns.recent].flatMap((group) => group.runs),
    [groupedRuns],
  );
  const derivedCropSourceIds = useMemo(
    () => new Set(
      runs
        .filter((run) =>
          run.derivedTransform === "crop_4_5_center" &&
          typeof run.derivedFromRunId === "string")
        .map((run) => run.derivedFromRunId!),
    ),
    [runs],
  );
  const expandedHistoryGroup = groupedRuns.recent.find(
    (group) => group.id === expandedHistoryId,
  );
  const runsToRender = legacyRunsResponse
    ? runs
    : [
        ...groupedRuns.active.flatMap((group) => group.runs),
        ...(expandedHistoryGroup?.runs || []),
      ];
  const runsInMainGrid = legacyRunsResponse
    ? runs
    : groupedRuns.active.flatMap((group) => group.runs);

  useEffect(() => {
    if (!cropFocusRunId) return;
    const focusedRun = runs.find((run) => run.id === cropFocusRunId);
    if (!focusedRun) return;
    const recentGroup = groupedRuns.recent.find((group) =>
      group.runs.some((run) => run.id === cropFocusRunId),
    );
    if (recentGroup && expandedHistoryId !== recentGroup.id) {
      setHistoryOpen(true);
      setExpandedHistoryId(recentGroup.id);
      return;
    }
    const focusStatus = `${focusedRun.status}:${recentGroup?.id || "active"}`;
    if (cropFocusStatusRef.current === focusStatus) return;
    const frame = window.requestAnimationFrame(() => {
      const card = document.getElementById(`video-reference-run-${cropFocusRunId}`);
      if (!card) return;
      cropFocusStatusRef.current = focusStatus;
      card.focus({ preventScroll: true });
      card.scrollIntoView?.({ behavior: "auto", block: "center" });
      if (focusedRun.status === "ready" || focusedRun.status === "failed") {
        setCropFocusRunId(null);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    cropFocusRunId,
    expandedHistoryId,
    groupedRuns.recent,
    historyResultsTarget,
    runs,
  ]);

  const referenceLibrary = useMemo(
    () => deriveReferenceLibrary(pastUploads, runs, favoriteSourceKeys, sourceDisplayNames),
    [pastUploads, runs, favoriteSourceKeys, sourceDisplayNames],
  );
  const visibleReferenceLibrary = typeSplitEnabled
    ? referenceLibrary.filter((item) => referenceMatchesType(item, promptType))
    : referenceLibrary;
  const recentReferences = visibleReferenceLibrary.slice(0, 4);
  const favoriteReferences = visibleReferenceLibrary.filter((item) => item.isFavorite);
  const referenceTemplates = useMemo(
    () => adaptVideoReferenceTemplates(referenceTemplatesData || []),
    [referenceTemplatesData],
  );
  const visibleReferenceTemplates = typeSplitEnabled
    ? referenceTemplates.filter((item) => referenceMatchesType(item, promptType))
    : referenceTemplates;
  const changePromptType = (next: VideoReferencePromptType) => {
    setPromptType(next);
    setReference((current) => current && !referenceMatchesType(current, next) ? null : current);
  };
  useEffect(() => {
    if (!typeSplitEnabled) {
      setPromptType("general");
      setPromotedItemType("product");
      setSelectedOfferId("");
    }
  }, [typeSplitEnabled]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [renamingSourceKey, setRenamingSourceKey] = useState<string | null>(null);
  const renameInFlightSourceKeysRef = useRef(new Set<string>());

  useEffect(() => {
    setSelectedCharacterId("");
    if (typeSplitEnabled) setModelReferenceOverrideUrl("");
  }, [selectedPersonaId]);

  useEffect(() => {
    if (fontAssetId) {
      if (fontAssets.some((asset) => asset.id === fontAssetId)) {
        if (pendingAssetSelectionRef.current === fontAssetId) {
          pendingAssetSelectionRef.current = null;
        }
      } else if (pendingAssetSelectionRef.current !== fontAssetId) {
        setFontAssetId("");
      }
    }
    if (
      musicAssetId !== "__auto__" &&
      !musicAssets.some((asset) => asset.id === musicAssetId)
    ) {
      if (pendingAssetSelectionRef.current !== musicAssetId) {
        setMusicAssetId("__auto__");
      }
    } else if (pendingAssetSelectionRef.current === musicAssetId) {
      pendingAssetSelectionRef.current = null;
    }
    if (quickMusicAssetId && !musicAssets.some((asset) => asset.id === quickMusicAssetId)) {
      if (pendingAssetSelectionRef.current !== quickMusicAssetId) {
        setQuickMusicAssetId("");
        setQuickAudio("keep");
      }
    } else if (pendingAssetSelectionRef.current === quickMusicAssetId) {
      pendingAssetSelectionRef.current = null;
    }
  }, [fontAssetId, fontAssets, musicAssetId, musicAssets, quickMusicAssetId]);

  useEffect(() => {
    if (typeof FontFace === "undefined") return;
    for (const asset of [selectedFontAsset, textEditSpecimenAsset]) {
      if (!asset?.previewUrl) continue;
      const family = `video-ref-picker-${asset.id}`;
      const face = new FontFace(family, `url("${asset.previewUrl}")`);
      face.load()
        .then((loaded) => document.fonts.add(loaded))
        .catch(() => undefined);
    }
  }, [
    selectedFontAsset?.id,
    selectedFontAsset?.previewUrl,
    textEditSpecimenAsset?.id,
    textEditSpecimenAsset?.previewUrl,
  ]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("video", file);
      const res = await fetch(`/api/brands/${brandId}/video-reference-ads/upload`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(msg.message || "Upload failed");
      }
      const data = await res.json();
      setReference({ ...data, fileName: file.name });
      queryClient.invalidateQueries({ queryKey: [uploadsKey] });
      queryClient.invalidateQueries({ queryKey: [referenceLibraryKey] });
      toast({ title: "Reference video ready", description: `${Math.round(data.durationSec)}s • ${data.width}×${data.height}` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleModelReferenceUpload = async (file: File) => {
    setUploadingModelReference(true);
    try {
      const body = new FormData();
      body.append("image", file);
      const response = await fetch(
        `/api/brands/${brandId}/video-reference-ads/model-reference-upload`,
        { method: "POST", body, credentials: "include" },
      );
      const data = await response.json().catch(() => ({ message: "Upload failed" }));
      if (!response.ok || typeof data.url !== "string" || !data.url.trim()) {
        throw new Error(data.message || "Upload failed");
      }
      setModelReferenceOverrideUrl(data.url.trim());
      toast({
        title: "Model photo ready",
        description: "This photo will replace the saved headshot for this run.",
      });
    } catch (error: any) {
      setModelReferenceOverrideUrl("");
      toast({
        title: "Model photo upload failed",
        description: error?.message || "Upload failed",
        variant: "destructive",
      });
    } finally {
      setUploadingModelReference(false);
      if (modelReferenceInputRef.current) modelReferenceInputRef.current.value = "";
    }
  };

  const handleAssetUpload = async (
    kind: "music",
    file: File,
    selectionTarget: "new-run" | "quick-edit" = "new-run",
  ) => {
    setUploadingAssetKind(kind);
    try {
      const fd = new FormData();
      fd.append("asset", file);
      fd.append("kind", kind);
      fd.append("label", file.name.replace(/\.[^.]+$/, "").trim() || file.name);
      const res = await fetch(`${assetsKey}/import`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const data = await res.json().catch(() => ({ message: "Upload failed" }));
      if (!res.ok) throw new Error(data.message || "Upload failed");
      const asset = data.asset as VideoReferenceAssetPublic;
      // Keep the just-admitted row selectable even if a transient picker
      // refetch fails or returns a stale edge-cache response.
      const mergeAdmittedAsset = () => {
        queryClient.setQueryData<VideoReferenceAssetPublic[]>(
          [assetsKey],
          (current = []) => current.some((item) => item.id === asset.id)
            ? current
            : [...current, asset],
        );
      };
      mergeAdmittedAsset();
      pendingAssetSelectionRef.current = asset.id;
      if (selectionTarget === "quick-edit") {
        setQuickMusicAssetId(asset.id);
        setQuickAudio("replace");
      } else {
        setMusicAssetId(asset.id);
      }
      await queryClient.invalidateQueries({ queryKey: [assetsKey] })
        .catch(() => undefined);
      // An active refetch may have returned a stale list that does not yet
      // include the successful import, so merge once more after it settles.
      mergeAdmittedAsset();
      toast({
        title: data.duplicate ? "Already in this brand's library" : "Music uploaded",
        description: data.message || `${asset.label} is selected for this video.`,
      });
    } catch (error: any) {
      toast({
        title: "Music upload failed",
        description: error?.message || "Upload failed",
        variant: "destructive",
      });
    } finally {
      setUploadingAssetKind(null);
      if (musicAssetInputRef.current) musicAssetInputRef.current.value = "";
      if (quickMusicAssetInputRef.current) quickMusicAssetInputRef.current.value = "";
    }
  };

  const importFontFromUrl = async (
    label: string,
    fontUrl: string,
    selectionTarget: "new-run" | "text-edit",
  ): Promise<string> => {
    const res = await fetch(`${assetsKey}/import-font`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label, fontUrl }),
      credentials: "include",
    });
    const data = await res.json().catch(() => ({ message: "Font import failed" }));
    if (!res.ok) throw new Error(data.message || "Font import failed");
    const asset = data.asset as VideoReferenceAssetPublic | undefined;
    if (!asset?.id || asset.kind !== "font") {
      throw new Error("The server did not return the imported font.");
    }
    const mergeImportedFont = () => {
      queryClient.setQueryData<VideoReferenceAssetPublic[]>(
        [assetsKey],
        (current = []) => current.some((item) => item.id === asset.id)
          ? current.map((item) => item.id === asset.id ? asset : item)
          : [...current, asset],
      );
    };
    mergeImportedFont();
    if (selectionTarget === "text-edit") {
      setTextEditFontChoice(asset.id);
    } else {
      pendingAssetSelectionRef.current = asset.id;
      setFontAssetId(asset.id);
    }
    await queryClient.invalidateQueries({ queryKey: [assetsKey] }).catch(() => undefined);
    mergeImportedFont();
    const message = data.message || `${asset.label} was validated, imported, and selected.`;
    toast({
      title: data.duplicate ? "Font already available" : "Font imported",
      description: message,
    });
    return message;
  };

  const importFontFromGoogle = async (
    family: string,
    selectionTarget: "new-run" | "text-edit",
  ): Promise<string> => {
    const res = await fetch(`${assetsKey}/import-google-font`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ family }),
      credentials: "include",
    });
    const data = await res.json().catch(() => ({ message: "Google Font import failed" }));
    if (!res.ok) throw new Error(data.message || "Google Font import failed");
    const asset = data.asset as VideoReferenceAssetPublic | undefined;
    if (!asset?.id || asset.kind !== "font") {
      throw new Error("The server did not return the imported Google Font.");
    }
    const mergeImportedFont = () => {
      queryClient.setQueryData<VideoReferenceAssetPublic[]>(
        [assetsKey],
        (current = []) => current.some((item) => item.id === asset.id)
          ? current.map((item) => item.id === asset.id ? asset : item)
          : [...current, asset],
      );
    };
    mergeImportedFont();
    if (selectionTarget === "text-edit") {
      setTextEditFontChoice(asset.id);
    } else {
      pendingAssetSelectionRef.current = asset.id;
      setFontAssetId(asset.id);
    }
    await queryClient.invalidateQueries({ queryKey: [assetsKey] }).catch(() => undefined);
    mergeImportedFont();
    const message = `${asset.label} is selected and ready for rendering.`;
    toast({
      title: "Font selected",
      description: message,
    });
    return message;
  };

  const handleFontPickerChange = async (
    value: string,
    selectionTarget: "new-run" | "text-edit",
  ) => {
    if (!value.startsWith(GOOGLE_FONT_OPTION_PREFIX)) {
      const nextValue =
        selectionTarget === "new-run" && value === DEFAULT_FONT ? "" : value;
      if (selectionTarget === "text-edit") {
        setTextEditFontChoice(nextValue);
      } else {
        setFontAssetId(nextValue);
      }
      return;
    }

    const family = value.slice(GOOGLE_FONT_OPTION_PREFIX.length);
    setPendingGoogleFont({ target: selectionTarget, family });
    try {
      await importFontFromGoogle(family, selectionTarget);
    } catch (error: any) {
      toast({
        title: "Google Font unavailable",
        description: error?.message || "The selected Google Font could not be saved.",
        variant: "destructive",
      });
    } finally {
      setPendingGoogleFont(null);
    }
  };

  const pickerValueFor = (
    selectionTarget: "new-run" | "text-edit",
    value: string,
  ) => pendingGoogleFont?.target === selectionTarget
    ? googleFontOptionValue(pendingGoogleFont.family)
    : value;

  const selectReference = (item: ReferenceLibraryItem) => {
    setReference(referenceFromLibraryItem(item));
    setLibraryOpen(false);
  };
  const updateReferenceFavorite = useMutation({
    mutationFn: async ({ item, favorite }: { item: ReferenceLibraryItem; favorite: boolean }) => {
      const res = await fetch(`${referenceLibraryKey}/favorite`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sourceKey: item.key, favorite }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Could not update favorite");
      return data as { sourceKey: string; favorite: boolean };
    },
    onSuccess: (result) => {
      queryClient.setQueryData<VideoReferenceLibraryResponse>([referenceLibraryKey], (current) => {
        if (!current) return current;
        const keys = new Set(current.favoriteSourceKeys || []);
        if (result.favorite) keys.add(result.sourceKey);
        else keys.delete(result.sourceKey);
        return { ...current, favoriteSourceKeys: [...keys] };
      });
    },
    onError: (error: any) => {
      toast({
        title: "Could not update favorite",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const renameReference = useMutation({
    mutationFn: async ({ item, displayName }: { item: ReferenceLibraryItem; displayName: string }) => {
      const res = await fetch(`${referenceLibraryKey}/rename`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sourceKey: item.key, displayName }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Could not rename reference");
      return data as { sourceKey: string; displayName: string };
    },
    onMutate: ({ item }) => {
      setRenamingSourceKey(item.key);
    },
    onSuccess: (result) => {
      queryClient.setQueryData<VideoReferenceLibraryResponse>([referenceLibraryKey], (current) => {
        if (!current) return current;
        const nextDisplayNames = {
          ...(current.sourceDisplayNames || {}),
          [result.sourceKey]: result.displayName,
        };
        return {
          ...current,
          sourceDisplayNames: nextDisplayNames,
          uploads: current.uploads.map((upload) =>
            normalizeReferenceSourceIdentity(upload.url) === result.sourceKey
              ? { ...upload, fileName: result.displayName }
              : upload,
          ),
        };
      });
      setReference((current) => (
        current && normalizeReferenceSourceIdentity(current.url) === result.sourceKey
          ? { ...current, fileName: result.displayName }
          : current
      ));
      toast({ title: "Reference renamed", description: result.displayName });
    },
    onError: (error: any) => {
      toast({
        title: "Could not rename reference",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setRenamingSourceKey(null);
    },
  });

  const renameReferenceItem = async (item: ReferenceLibraryItem, displayName: string) => {
    if (renameInFlightSourceKeysRef.current.has(item.key)) {
      throw new Error("A rename is already in progress for this reference.");
    }
    renameInFlightSourceKeysRef.current.add(item.key);
    try {
      await renameReference.mutateAsync({ item, displayName });
    } finally {
      renameInFlightSourceKeysRef.current.delete(item.key);
    }
  };

  const resetGenerationForm = () => {
    setReference(null);
    setSelectedProductId("");
    setSelectedOfferId("");
    setPromotedItemType("product");
    setPromptType("general");
    setSelectedVariantName(null);
    setVariant("");
    setExactText("");
    setSelectedPersonaId("");
    setSelectedCharacterId("");
    setModelReferenceOverrideUrl("");
    setFontAssetId("");
    setTextPlacement("top");
    setMusicAssetId("__auto__");
    setBatchVariants([]);
    setBatchSource(null);
    setBatchFileName("");
    setBatchRequestId("");
    if (configurationMode === "json") {
      const params = new URLSearchParams(search);
      params.delete("mode");
      setLocation(`${location}${params.size ? `?${params}` : ""}`);
    }
    pendingAssetSelectionRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (variantsInputRef.current) variantsInputRef.current.value = "";
    if (modelReferenceInputRef.current) modelReferenceInputRef.current.value = "";
  };

  const createRun = useMutation({
    mutationFn: async () => {
      if (!reference) throw new Error("Upload a reference video first");
      const res = await apiRequest("POST", runsKey, {
        ...buildVideoReferenceAdRequestBase({
          productId: typeSplitEnabled && promotedItemType !== "product" ? "" : selectedProductId,
          selectedVariantName: typeSplitEnabled && promotedItemType !== "product" ? null : selectedVariantName,
          personaId: selectedPersonaId,
          characterId: selectedCharacterId,
          reference,
        }),
        ...(typeSplitEnabled ? {
          promptType,
          promotedItemType,
          ...(promotedItemType === "offer" ? { offerId: selectedOfferId } : {}),
        } : {}),
        variant: variant.trim() || undefined,
        ...(modelReferenceOverrideUrl ? { modelReferenceOverrideUrl } : {}),
        exactText: exactText === "" ? undefined : exactText,
        // Font / placement — only when exactText is set
        ...(hasExactText && fontAssetId ? { fontAssetId } : {}),
        ...(hasExactText ? { textPlacement } : {}),
        ...(hasExactText ? {
          textStyle: {
            version: 1,
            fontSize: DEFAULT_EXACT_TEXT_FONT_SIZE,
            maxWidth: DEFAULT_EXACT_TEXT_MAX_WIDTH_PERCENT / 100,
          },
        } : {}),
        // Music — omit sentinel "__auto__" (server default)
        ...(musicAssetId && musicAssetId !== "__auto__" ? { musicAssetId } : {}),
      });
      return res.json();
    },
    onSuccess: (data: SanitizedRun) => {
      resetGenerationForm();
      queryClient.invalidateQueries({ queryKey: [runsKey] });
      if (data.status === "queued") {
        toast({
          title: "Run queued",
          description: "It will start automatically when an earlier video finishes.",
        });
      } else {
        toast({ title: "Generation started", description: typeSplitEnabled
          ? "Preparing your video…"
          : "Preparing a custom product model…" });
      }
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e?.message || "Failed to create run", variant: "destructive" });
    },
  });

  const createBatch = useMutation({
    mutationFn: async () => {
      if (!batchRequestId) throw new Error("Upload the variants JSON again");
      if (batchSource === null || !batchResolution.resolution) {
        throw new Error(batchResolution.error || "Could not resolve the imported variants");
      }
      const res = await apiRequest("POST", `${runsKey}/batch`, {
        batchId: batchRequestId,
        items: batchSource,
        defaults: batchDefaults,
      });
      return res.json();
    },
    onSuccess: (data: { acceptedCount?: number }) => {
      resetGenerationForm();
      queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({
        title: `${data.acceptedCount || batchVariants.length} videos queued`,
        description: `Up to 3 will generate at a time.`,
      });
    },
    onError: (e: any) => {
      toast({ title: "Could not start batch", description: e?.message || "Batch failed", variant: "destructive" });
    },
  });

  const retryRun = useMutation({
    mutationFn: async (runId: string) => {
      const res = await apiRequest("POST", `${runsKey}/${runId}/retry`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({ title: "Retrying run" });
    },
    onError: (e: any) => {
      toast({ title: "Retry failed", description: e?.message || "Failed to retry", variant: "destructive" });
    },
  });

  const duplicateCrop = useMutation({
    mutationFn: async (runId: string) => {
      const response = await apiRequest(
        "POST",
        `${runsKey}/${runId}/duplicate-crop-4-5`,
        {},
      );
      return response.json() as Promise<SanitizedRun>;
    },
    onSuccess: (derived) => {
      if (derived?.id) {
        cropFocusStatusRef.current = null;
        setCropFocusRunId(derived.id);
        queryClient.setQueryData<VideoReferenceRunsData>([runsKey], (current) => {
          const rows = Array.isArray(current)
            ? current
            : [...(current?.active || []), ...(current?.recent || [])]
                .flatMap((group) => group.runs);
          const next = [...rows.filter((run) => run.id !== derived.id), derived];
          return Array.isArray(current) ? next : groupVideoReferenceRuns(next, isRunViewActive);
        });
      }
      toast({
        title: "Creating 4:5 duplicate",
        description: "The finished MP4 is being center-cropped. Your original video is retained.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Could not create 4:5 duplicate",
        description: error?.message || "The crop could not start",
        variant: "destructive",
      });
    },
  });

  const regenerateText = useMutation({
    mutationFn: async () => {
      if (!textEditTarget) throw new Error("Choose a completed video");
      if (quickAudio === "replace" && !quickMusicAssetId) {
        throw new Error("Choose an active music track before replacing the soundtrack");
      }
      await queryClient.cancelQueries({ queryKey: [runsKey] });
      const response = await apiRequest(
        "POST",
        `${runsKey}/${textEditTarget.id}/regenerate-text`,
        {
          exactText: textEditValue,
          textPlacement: textEditPlacement,
          fontSize: textEditFontSize,
          maxWidth: textEditMaxWidthPercent / 100,
          ...(textEditFontChoice === DEFAULT_FONT
            ? { fontIntent: "default" }
            : textEditFontChoice === KEEP_CURRENT_FONT
              ? {}
              : { fontAssetId: textEditFontChoice }),
        },
      );
      if (response.status !== 202) {
        throw new Error(
          `Text regeneration was not accepted (expected 202, received ${response.status}). Try again.`,
        );
      }
      return response.json();
    },
    onSuccess: async (data: SanitizedRun) => {
      await queryClient.cancelQueries({ queryKey: [runsKey] });
      if (data?.id) {
        queryClient.setQueryData<VideoReferenceRunsData>([runsKey], (current) =>
          updateRunsData(current, (run) =>
            run.id === data.id ? { ...run, ...data } : run),
        );
      }
      setTextEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({
        title: "Updating video text",
        description: "The clean source and approved soundtrack are being reused.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Could not update text",
        description: error?.message || "Text regeneration failed",
        variant: "destructive",
      });
    },
  });

  const quickEdit = useMutation({
    mutationFn: async () => {
      if (!textEditTarget) throw new Error("Choose a completed video");
      await queryClient.cancelQueries({ queryKey: [runsKey] });
      const response = await fetch(`${runsKey}/${textEditTarget.id}/quick-edit`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          exactText: textEditValue.trim() ? textEditValue : null,
          trimStartSec: quickEditSourceOffset + quickTrimStart,
          trimEndSec: quickEditSourceOffset + quickTrimEnd,
          audio: quickAudio,
          ...(quickAudio === "replace" ? { musicAssetId: quickMusicAssetId } : {}),
          textPosition: clampQuickEditTextPosition(
            resolveQuickEditTextPosition(
              textEditPosition,
              textEditPlacement,
              textEditTarget.textStyleJson as Record<string, unknown> | null,
              textEditPositionWasDragged,
            ),
          ),
          ...(textEditValue.trim()
            ? {
                textStartSec: quickEditSourceOffset + quickTextStart,
                textEndSec: quickEditSourceOffset + quickTextEnd,
                textColor: quickTextColor,
                fontWeight: quickTextWeight,
                textAlign: quickTextAlign,
              }
            : {}),
          fontSize: textEditFontSize,
          maxWidth: textEditMaxWidthPercent / 100,
          ...(textEditFontChoice === DEFAULT_FONT
            ? { fontIntent: "default" }
            : textEditFontChoice === KEEP_CURRENT_FONT
              ? {}
              : { fontAssetId: textEditFontChoice }),
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          typeof data?.message === "string"
            ? data.message
            : `Quick edit could not start (${response.status}). Try again.`,
        );
      }
      if (response.status !== 202) {
        throw new Error(
          `Quick edit was not accepted (expected 202, received ${response.status}). Try again.`,
        );
      }
      return data;
    },
    onSuccess: async (data: SanitizedRun) => {
      await queryClient.cancelQueries({ queryKey: [runsKey] });
      if (data?.id) {
        queryClient.setQueryData<VideoReferenceRunsData>([runsKey], (current) =>
          updateRunsData(current, (run) =>
            run.id === data.id ? { ...run, ...data } : run),
        );
      }
      setTextEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({
        title: "Applying quick edit",
        description: "The clean source is rendering once. Your previous video remains playable until the new MP4 is ready.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Could not apply quick edit",
        description: quickEditErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const deleteRun = useMutation({
    mutationFn: async (runId: string) => {
      const res = await apiRequest("DELETE", `${runsKey}/${runId}`);
      return res.json();
    },
    onSuccess: (_data, runId) => {
      queryClient.setQueryData<VideoReferenceRunsData>(
        [runsKey],
        (current) => updateRunsData(current, (run) => run.id === runId ? null : run),
      );
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({ title: "Video deleted" });
    },
    onError: (e: any) => {
      queryClient.invalidateQueries({ queryKey: [runsKey] });
      toast({
        title: "Could not delete video",
        description: e?.message || "Failed to delete video",
        variant: "destructive",
      });
    },
  });

  const saveRun = useMutation({
    mutationFn: async (runId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/library/save-video-reference-ad`,
        { runId },
      );
      return res.json() as Promise<{ alreadySaved?: boolean }>;
    },
    onSuccess: (data, runId) => {
      setSavedRunIds((current) => new Set(current).add(runId));
      invalidateGeneratedAdListings();
      toast({
        title: data.alreadySaved ? "Already saved to My Ads" : "Saved to My Ads",
        description: "Find it in the Videos category.",
      });
    },
    onError: (e: any) => {
      toast({
        title: "Could not save video",
        description: e?.message || "Failed to save video",
        variant: "destructive",
      });
    },
  });

  // Active result cards do not own the setup form. Keep the form locked only
  // while this page is admitting a submission or processing an input.
  const formLocked =
    createRun.isPending ||
    createBatch.isPending ||
    uploading ||
    uploadingModelReference ||
    batchFileLoading ||
    uploadingAssetKind !== null ||
    pendingGoogleFont !== null;
  const canGenerate =
    (configurationMode === "manual"
      ? !!reference && (typeSplitEnabled
        ? (promotedItemType === "none" || (promotedItemType === "offer" ? !!selectedOfferId : !!selectedProductId)) &&
          (!selectedPersonaId || !!selectedCharacterId)
        : !!selectedProductId && !!selectedPersonaId && !!selectedCharacterId)
      : batchVariants.length > 0 && !!batchResolution.resolution) &&
    !formLocked;

  const setQuickTrimStartBounded = (value: number, duration: number) => {
    const next = Math.max(0, Math.min(value, Math.max(0, quickTrimEnd - 0.25)));
    setQuickTrimStart(next);
    setQuickTextStart((current) => Math.max(current, next));
    setQuickTextEnd((current) => Math.max(
      current,
      Math.min(duration, next + 0.25),
    ));
    setQuickPlayhead((current) => {
      const clamped = Math.max(next, Math.min(quickTrimEnd, current));
      const video = quickEditVideoRef.current;
      if (video && Math.abs(clamped - current) > 0.001) {
        video.pause();
        video.currentTime = quickEditSourceOffset + clamped;
      }
      return clamped;
    });
  };
  const setQuickTrimEndBounded = (value: number, duration: number) => {
    const next = Math.max(quickTrimStart + 0.25, Math.min(value, duration));
    setQuickTrimEnd(next);
    setQuickTextStart((current) => Math.min(current, Math.max(quickTrimStart, next - 0.25)));
    setQuickTextEnd((current) => Math.max(
      Math.min(current, next),
      Math.min(next, quickTextStart + 0.25),
    ));
    setQuickPlayhead((current) => {
      const clamped = Math.max(quickTrimStart, Math.min(next, current));
      const video = quickEditVideoRef.current;
      if (video && Math.abs(clamped - current) > 0.001) {
        video.pause();
        video.currentTime = quickEditSourceOffset + clamped;
      }
      return clamped;
    });
  };

  const productOptions = useMemo(
    () =>
      [...products]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
        .map((p) => {
          const media = (p as { mediaJson?: string[] | null }).mediaJson;
          const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
          return { value: p.id, label: p.name, imageUrl: first };
        }),
    [products],
  );
  const availablePersonas = useMemo(() => selectablePersonas(personas), [personas]);
  const personaOptions = useMemo(
    () => [...availablePersonas]
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
      .map((persona) => ({ value: persona.id, label: persona.name, imageUrl: persona.imageUrl })),
    [availablePersonas],
  );
  const selectedPersona = availablePersonas.find((persona) => persona.id === selectedPersonaId);
  const readyCharacters = personaCharacters.filter(
    (character) => character.avatarStatus === "ready" && !!character.avatarUrl,
  );
  const selectedCharacter = readyCharacters.find(
    (character) => character.id === selectedCharacterId,
  );
  const unavailableCharacterCount = personaCharacters.length - readyCharacters.length;
  const legacyStarterPrompt = composeVideoReferenceStarterPrompt({
    products,
    personas: availablePersonas,
    selectedPersona,
    readyCharacters,
    selectedPersonaCharactersLoaded: !!selectedPersonaId && !charactersLoading,
    fontAssets,
    musicAssets,
    reference,
  });
  const starterPrompt = typeSplitEnabled
    ? `${legacyStarterPrompt}

For this brand, each item or top-level defaults can set promptType to general, ugc, or 3d and promotedItemType to product, offer, or none. Every item requires a trusted referenceUploadId. Product requires productId, Offer requires offerId, and None requires neither. Persona and ready model are optional together; do not include personaId without characterId.
Available offers:
${offers.length ? offers.map((offer) => `- ${offer.id}: ${offer.name}`).join("\n") : "No offers are currently loaded; do not invent offer IDs."}
The legacy example above shows a product run; adapt its fields to the selected promotion mode.`
    : legacyStarterPrompt;
  const copyStarterPrompt = async () => {
    try {
      await navigator.clipboard.writeText(starterPrompt);
      toast({ title: "Starter prompt copied" });
    } catch (error) {
      toast({
        title: "Could not copy starter prompt",
        description: error instanceof Error ? error.message : "Clipboard access was denied.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-3.5 px-3 py-4 sm:px-6 lg:px-8" data-testid="video-reference-creation-area">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Clapperboard className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight tracking-tight" data-testid="text-page-title">Video Reference Ads</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {typeSplitEnabled
              ? "Upload a reference video and create an original video in the same style, pacing, and format."
              : "Upload a reference video and create an original product ad in the same style, pacing, and format."}
          </p>
        </div>
      </div>
      {canConfigurePrompts && typeSplitEnabled && (
        <section className="rounded-xl border border-violet-200/80 bg-white/80 p-3 shadow-sm dark:border-violet-900/50 dark:bg-card" aria-label="Brand prompts" data-testid="section-vra-type-prompts">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold">Brand prompts</h2>
              <p className="text-xs text-muted-foreground">Configure General, UGC, and 3D prompts independently for this brand.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-violet-200 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"
              onClick={() => { setPromptConfigurationType("general"); setPromptConfigurationOpen(true); }}
              data-testid="button-manage-vra-prompts"
            >
              <Settings2 className="mr-1.5 h-4 w-4" />
              Manage brand prompts
            </Button>
          </div>
        </section>
      )}
      {canConfigurePrompts && (
        <VideoReferenceBrandPromptDialog
          brandId={brandId}
          typeSplitEnabled={typeSplitEnabled}
          initialType={promptConfigurationType}
          open={promptConfigurationOpen}
          onOpenChange={setPromptConfigurationOpen}
        />
      )}

      {/* Core inputs stay first so the required selection order is obvious. */}
      {configurationMode === "manual" && (
      <>
      <Card className="border-violet-100 bg-white/90 shadow-sm dark:border-violet-950/70 dark:bg-card" data-testid="card-set-inputs">
        <CardContent className="space-y-3.5 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-violet-50 dark:bg-violet-500">
              <span className="text-xs font-bold">2</span>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold">Set your inputs</p>
              <p className="text-xs text-muted-foreground">{typeSplitEnabled
                ? "Choose what to promote and optionally add a persona with a ready model. A reference video is always required."
                : "Choose a persona and product, then select one of that persona's ready models."}</p>
            </div>
          </div>
          {typeSplitEnabled && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Video type</p>
              <div className="flex gap-1 rounded-full border bg-muted/40 p-1 w-fit" role="group" aria-label="Video type" data-testid="toggle-video-type">
                {(["general", "ugc", "3d"] as const).map((type) => (
                  <Button key={type} type="button" size="sm" className="h-7 rounded-full px-3 text-xs"
                    variant={promptType === type ? "default" : "ghost"}
                    onClick={() => changePromptType(type)} disabled={formLocked}
                    data-testid={`toggle-video-type-${type}`}>
                    {type === "3d" ? "3D" : type === "ugc" ? "UGC" : "General"}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {typeSplitEnabled && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Promote</p>
              <div className="flex gap-1 rounded-full border bg-muted/40 p-1 w-fit" role="group" aria-label="Promoted item" data-testid="toggle-promoted-item">
                {(["product", "offer", "none"] as const).map((mode) => (
                  <Button key={mode} type="button" size="sm" className="h-7 rounded-full px-3 text-xs"
                    variant={promotedItemType === mode ? "default" : "ghost"} disabled={formLocked}
                    onClick={() => {
                      setPromotedItemType(mode);
                      setSelectedProductId("");
                      setSelectedVariantName(null);
                      setSelectedOfferId("");
                    }}
                    data-testid={`toggle-promoted-${mode}`}>
                    {mode === "product" ? <Package className="mr-1 h-3 w-3" /> : mode === "offer" ? <Sparkles className="mr-1 h-3 w-3" /> : null}
                    {mode === "none" ? "None" : mode === "offer" ? "Offer" : "Product"}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <PickerSelect
              icon={<UserCircle className="h-4 w-4" />}
               label={typeSplitEnabled ? "Persona (optional)" : "Persona"}
               placeholder={personasLoading ? "Loading…" : typeSplitEnabled ? "No persona" : "Choose persona"}
              value={selectedPersonaId}
              onChange={setSelectedPersonaId}
               onClear={() => setSelectedPersonaId("")}
              options={personaOptions}
              testId="select-persona"
              searchable
              disabled={personasLoading || formLocked}
            />
             {(!typeSplitEnabled || promotedItemType === "product") && <PickerSelect
              icon={<Package className="h-4 w-4" />}
              label="Product"
              placeholder={productsLoading ? "Loading…" : "Choose product"}
              value={selectedProductId}
              onChange={(productId) => {
                setSelectedProductId(productId);
                setSelectedVariantName(null);
              }}
              onClear={() => {
                setSelectedProductId("");
                setSelectedVariantName(null);
              }}
              options={productOptions}
              testId="select-product"
              searchable
              disabled={productsLoading || formLocked}
             />}
             {typeSplitEnabled && promotedItemType === "offer" && <PickerSelect
               icon={<Sparkles className="h-4 w-4" />}
               label="Offer"
               placeholder={offersLoading ? "Loading…" : offers.length ? "Choose offer" : "No offers yet — add one in Offers"}
               value={selectedOfferId}
               onChange={setSelectedOfferId}
               onClear={() => setSelectedOfferId("")}
               options={offers.map((offer) => ({ value: offer.id, label: offer.name }))}
               testId="select-offer"
               searchable
               disabled={offersLoading || formLocked || offers.length === 0}
             />}
          </div>
          {(!typeSplitEnabled || promotedItemType === "product") && <VariantPicker
            product={selectedProduct}
            value={selectedVariantName}
            onChange={setSelectedVariantName}
            disabled={formLocked}
            label="Catalog variant (optional)"
            testIdPrefix="video-reference-product-variant"
          />}
          {typeSplitEnabled && promotedItemType === "offer" && !offersLoading && offers.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="text-no-offers">No offers are available for this brand. Add one in Offers to use Offer mode.</p>
          )}
          {!personasLoading && availablePersonas.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="text-no-personas">
              No selectable personas are available for this brand yet.
            </p>
          )}

          {selectedPersonaId && (
            <div className="space-y-3 rounded-xl border border-border/60 bg-muted/[0.18] p-4" data-testid="card-persona-models">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400">
                  <UserCircle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Saved persona models</div>
                  <p className="text-xs text-muted-foreground">
                    {charactersLoading
                      ? "Loading saved models…"
                      : personaCharacters.length === 0
                        ? `No models have been created for ${selectedPersona?.name || "this persona"} yet.`
                        : readyCharacters.length === 0
                          ? `No ready model headshots are available for ${selectedPersona?.name || "this persona"} yet.`
                           : typeSplitEnabled
                             ? `Select a ready model from ${selectedPersona?.name || "this persona"}, or clear the persona to run without one.`
                             : `Select one ready model from ${selectedPersona?.name || "this persona"} for this ad.`}
                  </p>
                </div>
              </div>

              {charactersLoading && (
                <div className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 bg-background/50 p-3 text-xs text-muted-foreground" data-testid="text-persona-model-loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading saved models…
                </div>
              )}
              {!charactersLoading && readyCharacters.length > 0 && (
                <div className="space-y-3">
                {selectedCharacter && (
                  <div
                    className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 rounded-xl border border-primary/30 bg-background p-3 sm:grid-cols-[112px_minmax(0,1fr)]"
                    data-testid="selected-persona-model-preview"
                  >
                    <img
                      src={selectedCharacter.avatarUrl!}
                      alt={`Selected model: ${selectedCharacter.name}`}
                      className="aspect-square w-full self-center rounded-lg object-cover"
                      data-testid="image-selected-persona-model"
                    />
                    <div className="flex min-w-0 flex-col justify-center">
                      <div className="mb-1 flex flex-wrap-reverse items-center justify-between gap-1.5">
                        <p className="text-sm font-semibold sm:text-base" data-testid="text-selected-persona-model-name">
                          {selectedCharacter.name}
                        </p>
                        <Badge
                          variant="secondary"
                          className="shrink-0 gap-1 text-[10px] font-medium"
                          data-testid="badge-selected-persona-model"
                        >
                          Saved model selected
                          <Check className="h-3 w-3" />
                        </Badge>
                      </div>
                      <p className="text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
                        Review this face before generating. If there is a likeness concern, choose another
                        saved model, manage or regenerate this model, or use a one-off photo below.
                      </p>
                      <Button asChild type="button" variant="outline" size="sm" className="mt-2 h-8 w-fit px-2.5 text-xs">
                        <Link
                          href={`/brands/${brandId}/personas/${selectedPersonaId}?characterId=${encodeURIComponent(selectedCharacter.id)}&from=video-reference-ads`}
                          data-testid="button-manage-selected-persona-model"
                        >
                          <ExternalLink className="mr-1.5 h-4 w-4" />
                          Review or regenerate in Personas
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <p className="mb-2 text-xs font-semibold">Choose from all ready saved models</p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                  {readyCharacters.map((character) => {
                    const isSelected = character.id === selectedCharacterId;
                    return (
                      <button
                        key={character.id}
                        type="button"
                        disabled={formLocked}
                         onClick={() => setSelectedCharacterId(isSelected && typeSplitEnabled ? "" : character.id)}
                        className={[
                          "group relative overflow-hidden rounded-xl border text-left transition-all disabled:cursor-not-allowed disabled:opacity-60",
                          isSelected
                            ? "border-primary ring-2 ring-primary/40 shadow-sm"
                            : "border-border/50 bg-background hover:border-primary/40 hover:shadow-sm",
                        ].join(" ")}
                        data-testid={`persona-model-card-${character.id}`}
                      >
                        <div className="relative aspect-square overflow-hidden bg-muted/40">
                          <img
                            src={character.avatarUrl!}
                            alt={character.name}
                            className="absolute inset-0 h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="bg-background px-1.5 py-1">
                          <p className="break-words text-[11px] font-semibold leading-tight" title={character.name}>
                            {character.name}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground shadow">
                            ✓
                          </div>
                        )}
                      </button>
                    );
                  })}
                  </div>
                </div>
                </div>
              )}
              {!charactersLoading && unavailableCharacterCount > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="text-unavailable-persona-models">
                  {unavailableCharacterCount} {unavailableCharacterCount === 1 ? "model is" : "models are"} unavailable until a ready headshot is generated.
                </p>
              )}
              <div className="space-y-2 border-t border-border/50 pt-3" data-testid="section-model-reference-override">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold">One-off model photo for this ad (optional)</p>
                    <p className="text-xs text-muted-foreground">
                      This does not change the saved persona model. It only overrides the visual identity for this new ad.
                    </p>
                  </div>
                  {!modelReferenceOverrideUrl && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={formLocked}
                      onClick={() => modelReferenceInputRef.current?.click()}
                      data-testid="button-upload-model-reference"
                    >
                      {uploadingModelReference
                        ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        : <Upload className="mr-1.5 h-4 w-4" />}
                      {uploadingModelReference ? "Uploading…" : "Upload photo"}
                    </Button>
                  )}
                </div>
                <input
                  ref={modelReferenceInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  disabled={formLocked}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void handleModelReferenceUpload(file);
                  }}
                  data-testid="input-model-reference"
                />
                {modelReferenceOverrideUrl && (
                  <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/[0.04] p-2">
                    <img
                      src={modelReferenceOverrideUrl}
                      alt="Uploaded model reference"
                      className="h-16 w-16 rounded-md object-cover"
                      data-testid="image-model-reference-preview"
                    />
                    <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                      Uploaded photo will be used as the identity reference.
                    </p>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      disabled={formLocked}
                      aria-label="Remove model photo"
                      onClick={() => {
                        setModelReferenceOverrideUrl("");
                        if (modelReferenceInputRef.current) modelReferenceInputRef.current.value = "";
                      }}
                      data-testid="button-remove-model-reference"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-violet-100 bg-white/90 shadow-sm dark:border-violet-950/70 dark:bg-card" data-testid="card-reference-video">
        <CardContent className="space-y-3.5 p-4 sm:p-5">
          <div className="flex min-w-0 items-start gap-3 border-b border-border/50 pb-3">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-violet-50 dark:bg-violet-500">
                <span className="text-xs font-bold">3</span>
              </div>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                <Video className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Reference video</p>
                <p className="text-xs text-muted-foreground">Upload a source video or reuse one saved for this brand.</p>
              </div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
            data-testid="input-reference-video"
          />
          <div className="rounded-xl border border-dashed border-violet-300 bg-violet-50/40 p-4 text-center dark:border-violet-800 dark:bg-violet-950/20">
            <Upload className="mx-auto mb-2 h-6 w-6 text-violet-600" aria-hidden="true" />
            <p className="text-sm font-medium">Add your reference video</p>
            <p className="mt-1 text-xs text-muted-foreground">MP4, MOV or WebM</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3 border-violet-200 bg-background text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300"
              onClick={() => fileInputRef.current?.click()}
              disabled={formLocked || uploading}
              data-testid="button-upload-reference"
            >
              {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
              {uploading ? "Processing…" : "Upload video"}
            </Button>
          </div>
          {reference && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.04] p-2.5" data-testid="selected-reference-summary">
                <video
                  src={reference.url}
                  className="h-16 w-12 shrink-0 rounded-md bg-black object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="video-reference-preview"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                    <div className="truncate text-sm font-medium">{reference.fileName}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {Math.round(reference.durationSec)}s{reference.width > 0 ? ` • ${reference.width}×${reference.height}` : ""} — the generated ad will match this length
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="hidden sm:inline-flex"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={formLocked}
                    data-testid="button-replace-reference"
                  >
                    {uploading ? (
                      <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Processing…</>
                    ) : (
                      <><Upload className="mr-1 h-4 w-4" /> Replace</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setReference(null)}
                    disabled={formLocked}
                    data-testid="button-clear-reference"
                  >
                    <X className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Remove</span>
                  </Button>
                </div>
              </div>
              {uploading && (
                <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="text-reference-uploading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing replacement video…
                </p>
              )}
            </div>
          )}
          <div className="space-y-3 pt-1" data-testid="section-recent-references">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">Recent reference videos</p>
                <p className="text-xs text-muted-foreground">
                  {visibleReferenceLibrary.length > 0
                    ? "Pick a recent source or browse the full library."
                    : "Your uploaded and used sources will appear here."}
                </p>
              </div>
              {visibleReferenceLibrary.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLibraryOpen(true)}
                  data-testid="button-view-all-references"
                >
                  View all <span className="ml-1 text-muted-foreground">({visibleReferenceLibrary.length})</span>
                </Button>
              )}
            </div>
            {uploadsLoading || runsLoading ? (
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 p-4 text-xs text-muted-foreground" data-testid="text-reference-library-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading reference videos…
              </div>
            ) : recentReferences.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-4 text-xs text-muted-foreground" data-testid="text-no-reference-library">
                No saved reference videos yet. Upload one above to start your library.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2.5 min-[560px]:grid-cols-3 xl:grid-cols-4" data-testid="grid-recent-references">
                {recentReferences.map((item, index) => (
                  <ReferenceLibraryCard
                    key={item.key}
                    item={item}
                    index={index}
                    context="recent"
                    isSelected={!!reference && normalizeReferenceSourceIdentity(reference.url) === item.key}
                    selectionDisabled={formLocked}
                    favoritePending={updateReferenceFavorite.isPending}
                    onSelect={selectReference}
                    onFavorite={(favoriteItem) => updateReferenceFavorite.mutate({
                      item: favoriteItem,
                      favorite: !favoriteItem.isFavorite,
                    })}
                    onRename={renameReferenceItem}
                    onPhonePreview={(previewItem) => setPhonePreview({
                      videoUrl: previewItem.url,
                      displayName: previewItem.fileName,
                    })}
                    renamePending={renamingSourceKey === item.key}
                  />
                ))}
              </div>
            )}
          </div>
          <details className="rounded-lg border border-border/60 p-3" data-testid="section-reference-templates">
            <summary className="cursor-pointer text-sm font-medium">Templates <span className="text-xs font-normal text-muted-foreground">· Curated reference videos</span></summary>
            <div className="mt-3">
              {templatesLoading ? (
                <p className="text-xs text-muted-foreground" data-testid="text-reference-templates-loading">Loading templates…</p>
              ) : templatesError ? (
                <p className="text-xs text-destructive" role="alert" data-testid="text-reference-templates-error">{templatesError.message || "Could not load reference templates."}</p>
              ) : visibleReferenceTemplates.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="text-no-reference-templates">No templates are available for this brand yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="grid-reference-templates">
                  {visibleReferenceTemplates.map((item, index) => (
                    <ReferenceLibraryCard key={item.key} item={item} index={index} context="template"
                      isSelected={!!reference && normalizeReferenceSourceIdentity(reference.url) === normalizeReferenceSourceIdentity(item.url)}
                      selectionDisabled={formLocked} favoritePending={false} onSelect={selectReference}
                      onFavorite={() => undefined} onRename={async () => undefined}
                      onPhonePreview={(previewItem) => setPhonePreview({ videoUrl: previewItem.url, displayName: previewItem.fileName })}
                      renamePending={false} />
                  ))}
                </div>
              )}
            </div>
          </details>
          {favoriteReferences.length > 0 && (
            <details className="rounded-lg border border-border/60 p-3" data-testid="section-favorite-references">
              <summary className="cursor-pointer text-sm font-medium">Favorite reference videos</summary>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="grid-favorite-references">
                {favoriteReferences.map((item, index) => (
                  <ReferenceLibraryCard key={item.key} item={item} index={index} context="favorite"
                    isSelected={!!reference && normalizeReferenceSourceIdentity(reference.url) === item.key}
                    selectionDisabled={formLocked} favoritePending={updateReferenceFavorite.isPending}
                    onSelect={selectReference}
                    onFavorite={(favoriteItem) => updateReferenceFavorite.mutate({ item: favoriteItem, favorite: !favoriteItem.isFavorite })}
                    onRename={renameReferenceItem}
                    onPhonePreview={(previewItem) => setPhonePreview({ videoUrl: previewItem.url, displayName: previewItem.fileName })}
                    renamePending={renamingSourceKey === item.key} />
                ))}
              </div>
            </details>
          )}
          <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
            <DialogContent className="flex max-h-[min(85vh,720px)] min-w-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
              <DialogHeader className="shrink-0 p-6 pb-4">
                <DialogTitle>Reference video library</DialogTitle>
                <DialogDescription>
                  Preview any saved source, then use it for this ad. Favorites are personal and videos are ordered by latest activity.
                </DialogDescription>
              </DialogHeader>
              <div className="min-h-0 overflow-y-auto px-6 pb-6">
                {favoriteReferences.length > 0 && (
                  <div className="mb-4">
                    <p className="mb-2 text-sm font-medium">Your favorites</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {favoriteReferences.map((item, index) => (
                        <ReferenceLibraryCard
                          key={item.key}
                          item={item}
                          index={index}
                          context="library"
                          isSelected={!!reference && normalizeReferenceSourceIdentity(reference.url) === item.key}
                          selectionDisabled={formLocked}
                          favoritePending={updateReferenceFavorite.isPending}
                          onSelect={selectReference}
                          onFavorite={(favoriteItem) => updateReferenceFavorite.mutate({
                            item: favoriteItem,
                            favorite: !favoriteItem.isFavorite,
                          })}
                          onRename={renameReferenceItem}
                          onPhonePreview={(previewItem) => setPhonePreview({
                            videoUrl: previewItem.url,
                            displayName: previewItem.fileName,
                          })}
                          renamePending={renamingSourceKey === item.key}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <p className="mb-2 text-sm font-medium">All reference videos</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleReferenceLibrary.map((item, index) => (
                    <ReferenceLibraryCard
                      key={item.key}
                      item={item}
                      index={index}
                      context="library"
                      isSelected={!!reference && normalizeReferenceSourceIdentity(reference.url) === item.key}
                      selectionDisabled={formLocked}
                      favoritePending={updateReferenceFavorite.isPending}
                      onSelect={selectReference}
                      onFavorite={(favoriteItem) => updateReferenceFavorite.mutate({
                        item: favoriteItem,
                        favorite: !favoriteItem.isFavorite,
                      })}
                      onRename={renameReferenceItem}
                      onPhonePreview={(previewItem) => setPhonePreview({
                        videoUrl: previewItem.url,
                        displayName: previewItem.fileName,
                      })}
                      renamePending={renamingSourceKey === item.key}
                    />
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      </>
      )}

      <Card className="border-violet-100 bg-white/90 shadow-sm dark:border-violet-950/70 dark:bg-card" data-testid="card-creative-direction">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-600 text-violet-50 dark:bg-violet-500">
              <span className="text-xs font-bold">4</span>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
              <Film className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold">
                  {configurationMode === "manual" ? "Creative direction" : "JSON batch configuration"}
                </p>
                {configurationMode === "manual" ? (
                  <span className="rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-600 dark:bg-violet-500/10 dark:text-violet-300">
                    Optional
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {configurationMode === "manual"
                  ? "Optionally steer this version and add exact on-video text."
                  : "Upload a complete configuration for every video in the batch."}
              </p>
            </div>
          </div>
          <div className="space-y-3" data-testid="tabs-video-reference-config">
            {configurationMode === "manual" && <div className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2" data-testid="section-creative-direction-inputs">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="video-reference-variant">
                Creative direction <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                id="video-reference-variant"
                value={variant}
                onChange={(event) => setVariant(event.target.value)}
                placeholder={'e.g. "LA nightlife" — reinterprets the concept in a new direction'}
                maxLength={500}
                disabled={formLocked}
                data-testid="input-variant"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <label className="text-sm font-medium" htmlFor="video-reference-exact-text">
                  Exact on-video text <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{exactText.length}/500</span>
              </div>
              <Textarea
                id="video-reference-exact-text"
                value={exactText}
                onChange={(event) => setExactText(event.target.value)}
                placeholder={"e.g. “30% OFF\nTHIS WEEKEND”"}
                maxLength={500}
                rows={3}
                disabled={formLocked}
                data-testid="input-exact-text"
              />
              <p className="text-xs text-muted-foreground">
                Added character-for-character, with line breaks and spacing preserved.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-2" data-testid="section-creative-direction-options">
          <div className="h-full space-y-3 rounded-xl border border-border/60 p-4" data-testid="section-text-style-controls">
              <p className="text-xs font-semibold flex items-center gap-1.5">
                <Type className="h-3.5 w-3.5" aria-hidden="true" />
                Text options
              </p>
              <p className="text-xs text-muted-foreground">
                These apply only to this manually configured video when it has on-screen text.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-muted-foreground" htmlFor="font-asset-select">
                    Font <span className="font-normal">(optional — default: Inter)</span>
                  </label>
                   <Select
                     value={pickerValueFor("new-run", fontAssetId) || DEFAULT_FONT}
                     onValueChange={(value) => {
                       void handleFontPickerChange(value, "new-run");
                     }}
                     onOpenChange={(open) => {
                       if (open) {
                         ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((font) => font.family));
                       }
                     }}
                     disabled={formLocked || pendingGoogleFont !== null}
                   >
                     <SelectTrigger
                       id="font-asset-select"
                       className="h-9 w-full"
                       data-testid="select-font-asset"
                       data-value={pickerValueFor("new-run", fontAssetId) || DEFAULT_FONT}
                     >
                       <SelectValue placeholder="Default (Inter)" />
                     </SelectTrigger>
                     <SelectContent className="max-h-72">
                       <SelectItem value={DEFAULT_FONT} data-value={DEFAULT_FONT}>
                         Default (Inter)
                       </SelectItem>
                       <SelectSeparator />
                       <VideoReferenceFontOptions fontAssets={fontAssets} />
                     </SelectContent>
                   </Select>
                  {pendingGoogleFont?.target === "new-run" ? (
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      Preparing {pendingGoogleFont.family}…
                    </p>
                  ) : null}
                  {selectedFontAsset ? (
                    <p
                      className="rounded-md bg-muted/40 px-3 py-2 text-lg"
                      style={{ fontFamily: `"video-ref-picker-${selectedFontAsset.id}", sans-serif` }}
                      data-testid="text-selected-font-specimen"
                    >
                      Aa Bb Cc 0123 — Your exact text
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                   <label className="block text-xs font-medium text-muted-foreground" htmlFor="text-placement-select">
                     Vertical placement
                   </label>
                  <select
                    id="text-placement-select"
                    value={textPlacement}
                    onChange={(event) =>
                      setTextPlacement(event.target.value as VideoReferenceTextPlacement)
                    }
                    disabled={formLocked}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="select-text-placement"
                  >
                    <option value="top">Top</option>
                    <option value="upper-third">Upper third</option>
                    <option value="middle">Middle</option>
                    <option value="lower-third">Lower third</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>
              </div>
          </div>

          {/* Music picker — shown in Manual options mode */}
          <div className="h-full space-y-3 rounded-xl border border-border/60 p-4" data-testid="section-music-picker">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium flex items-center gap-1.5" htmlFor="music-asset-select">
                <Music className="h-3.5 w-3.5" aria-hidden="true" />
                Background music
              </label>
              <input
                ref={musicAssetInputRef}
                type="file"
                accept=".mp3,.wav"
                className="hidden"
                disabled={formLocked || uploadingAssetKind !== null}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleAssetUpload("music", file);
                }}
                data-testid="input-upload-music-asset"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => musicAssetInputRef.current?.click()}
                disabled={formLocked || uploadingAssetKind !== null}
                data-testid="button-upload-music-asset"
              >
                {uploadingAssetKind === "music" ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="mr-1 h-3 w-3" />
                )}
                Upload music
              </Button>
            </div>
            <select
              id="music-asset-select"
              value={musicAssetId}
              onChange={(e) => setMusicAssetId(e.target.value)}
              disabled={formLocked}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="select-music-asset"
            >
              <option value="__auto__">Automatic (AI-generated)</option>
              {musicAssets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}{a.durationSec ? ` (${Math.round(a.durationSec)}s)` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              "Automatic" generates an original music track to match the video mood.
            </p>
            {selectedMusicAsset ? (
              <audio
                controls
                preload="none"
                src={selectedMusicAsset.previewUrl}
                className="h-9 w-full"
                aria-label={`Preview ${selectedMusicAsset.label}`}
                data-testid="audio-selected-music"
              />
            ) : null}
          </div>
          </div>
            </div>}

            {configurationMode === "json" && <div>
            <div className="space-y-3 rounded-xl border border-border/60 bg-muted/[0.18] p-4" data-testid="section-batch-variants">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="max-w-2xl text-xs text-muted-foreground">
                   {typeSplitEnabled
                     ? `Upload 1–${VIDEO_REFERENCE_BATCH_MAX_ITEMS} configurations. Each item (or top-level defaults) needs a trusted referenceUploadId. Choose promptType (general, ugc or 3d) and promotedItemType (product, offer or none) per item or in defaults. Product requires productId; Offer requires offerId; personaId and characterId are optional but must be paired.`
                     : `Upload 1–${VIDEO_REFERENCE_BATCH_MAX_ITEMS} complete video configurations. Each item (or top-level defaults) must identify its persona, ready model, product, trusted reference upload, and any creative, typography, or music choices. Creative direction and exact text are limited to ${VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS} characters each.`}
                </p>
                <input
                  ref={variantsInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  disabled={formLocked}
                  data-testid="input-variants-json"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                     setBatchFileLoading(true);
                    try {
                       const parsed = parseVideoReferenceVariantJsonWithSource(await file.text(), typeSplitEnabled);
                       setBatchVariants(parsed.variants);
                       setBatchSource(parsed.source);
                      setBatchFileName(file.name);
                      setBatchRequestId(crypto.randomUUID());
                       toast({ title: `${parsed.variants.length} variants loaded` });
                    } catch (error: any) {
                      setBatchVariants([]);
                       setBatchSource(null);
                      setBatchFileName("");
                      setBatchRequestId("");
                      toast({ title: "Invalid variants JSON", description: error.message, variant: "destructive" });
                    } finally {
                       setBatchFileLoading(false);
                      e.target.value = "";
                    }
                  }}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyStarterPrompt}
                    data-testid="button-export-video-reference-starter-prompt"
                  >
                    <Copy className="mr-1 h-4 w-4" />
                    Export starter prompt
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={formLocked}
                    onClick={() => variantsInputRef.current?.click()}
                    data-testid="button-upload-variants-json"
                  >
                     {batchFileLoading
                       ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                       : <FileJson className="mr-1 h-4 w-4" />}
                     {batchFileLoading ? "Reading JSON…" : "Upload JSON"}
                  </Button>
                </div>
              </div>
              {batchVariants.length > 0 && (
                <div className="space-y-2" data-testid="batch-variants-preview">
                  <div className="flex items-center justify-between text-sm">
                    <span><strong>{batchVariants.length}</strong> videos from {batchFileName}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                      setBatchVariants([]);
                       setBatchSource(null);
                      setBatchFileName("");
                      setBatchRequestId("");
                    }} disabled={formLocked}>Clear</Button>
                  </div>
                   {batchResolution.error ? (
                     <p className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive" data-testid="batch-resolution-error">
                       {batchResolution.error}
                     </p>
                   ) : null}
                   <div className="max-h-96 space-y-2 overflow-y-auto rounded bg-muted/40 p-2 text-xs">
                     {resolvedBatchVariants.map((item, index) => {
                       const sources = resolvedBatchSources[index] || {
                         font: "none",
                         placement: "none",
                         alignment: "none",
                         music: "automatic",
                       };
                       const fontAsset = item.fontAssetId
                         ? fontAssets.find((asset) => asset.id === item.fontAssetId)
                         : null;
                       const musicAsset = item.musicAssetId
                         ? musicAssets.find((asset) => asset.id === item.musicAssetId)
                         : null;
                       const sourceLabel = (source: string) => {
                          if (source === "form") return "Top-level JSON default";
                         if (source === "json") return "From JSON";
                         if (source === "textStyle") return "From JSON text style";
                         if (source === "automatic") return "Automatic";
                         return "Default";
                       };
                       const placement = item.textPlacement ||
                         (item.textStyle ? inferVideoReferenceTextPlacement(item.textStyle) : null);
                       const previewTextStyle = item.textStyle?.fontDataBase64
                         ? {
                             ...item.textStyle,
                             fontDataBase64: `[embedded font data: ${item.textStyle.fontDataBase64.length} Base64 characters]`,
                           }
                         : item.textStyle;
                        const productName =
                          products.find((product) => product.id === item.productId)?.name ||
                          null;
                        const offerName = offers.find((offer) => offer.id === item.offerId)?.name || item.offerId;
                        const resolvedEntryPreview = {
                           personaId: item.personaId || null,
                           characterId: item.characterId || null,
                          productId: item.productId || null,
                           ...(typeSplitEnabled ? {
                             promptType: item.promptType || "general",
                             promotedItemType: item.promotedItemType || "product",
                             offerId: item.offerId || null,
                           } : {}),
                          productName,
                          catalogVariantName: item.catalogVariantName ?? null,
                           referenceUploadId: item.referenceUploadId || null,
                          variant: item.variant,
                          exactText: item.exactText ?? null,
                          textStyle: previewTextStyle || null,
                          fontAssetId: item.fontAssetId || null,
                          textPlacement: item.textPlacement || null,
                          textAlign: item.textAlign || null,
                          musicAssetId: item.musicAssetId || null,
                        };
                       return (
                         <details
                           key={index}
                           className="rounded border border-border/50 bg-background/70 px-2 py-1.5"
                           data-testid={`batch-variant-preview-${index}`}
                         >
                            <summary className="cursor-pointer font-medium">
                                {index + 1}. {typeSplitEnabled && item.promotedItemType === "none"
                                  ? "Reference only"
                                  : typeSplitEnabled && item.promotedItemType === "offer"
                                    ? offerName || "Missing offer"
                                    : productName || item.productId || "Missing product"}
                           </summary>
                           <dl className="mt-2 grid gap-1 text-muted-foreground">
                              {typeSplitEnabled && <div>
                                <dt className="inline font-medium text-foreground">Type / promotion: </dt>
                                <dd className="inline">{item.promptType || "general"} / {item.promotedItemType || "product"}{item.promotedItemType === "offer" ? ` — ${offerName || "Missing offer"}` : ""}</dd>
                              </div>}
                              <div>
                                <dt className="inline font-medium text-foreground">Product: </dt>
                                   <dd className="inline">{productName || item.productId || (typeSplitEnabled && item.promotedItemType !== "product" ? "Not selected" : "Missing")}</dd>
                              </div>
                               <div>
                                 <dt className="inline font-medium text-foreground">Persona / model: </dt>
                                  <dd className="inline">{item.personaId || (typeSplitEnabled ? "None" : "Missing")} / {item.characterId || (typeSplitEnabled ? "None" : "Missing")}</dd>
                               </div>
                               <div>
                                 <dt className="inline font-medium text-foreground">Reference upload: </dt>
                                 <dd className="inline">{item.referenceUploadId || "Missing"}</dd>
                               </div>
                              <div>
                                <dt className="inline font-medium text-foreground">Catalog variant: </dt>
                                <dd className="inline">{item.catalogVariantName || "No specific catalog variant"}</dd>
                              </div>
                              <div>
                                <dt className="inline font-medium text-foreground">Creative direction: </dt>
                                <dd className="inline whitespace-pre-wrap">{item.variant || "None"}</dd>
                              </div>
                              <div>
                                <dt className="inline font-medium text-foreground">Exact on-video text: </dt>
                               <dd className="inline whitespace-pre-wrap">{item.exactText === undefined ? "None" : item.exactText || "Empty"}</dd>
                             </div>
                             <div>
                               <dt className="inline font-medium text-foreground">Font: </dt>
                               <dd className="inline">
                                 {item.exactText === undefined
                                   ? "Not applied (no on-screen text)"
                                   : item.fontAssetId
                                     ? `${fontAsset?.label || item.fontAssetId} — ${sourceLabel(sources.font)}`
                                     : item.textStyle?.fontFamily || item.textStyle?.fontUrl
                                       ? `${item.textStyle.fontFamily || item.textStyle.fontUrl} — ${sourceLabel(sources.font)}`
                                       : "Inter — Default"}
                               </dd>
                             </div>
                             <div>
                               <dt className="inline font-medium text-foreground">Music: </dt>
                               <dd className="inline">
                                 {item.musicAssetId
                                   ? `${musicAsset?.label || item.musicAssetId} — ${sourceLabel(sources.music)}`
                                   : "Automatic (AI-generated)"}
                               </dd>
                             </div>
                             <div>
                               <dt className="inline font-medium text-foreground">Placement: </dt>
                               <dd className="inline">
                                 {item.exactText === undefined
                                   ? "Not applied (no on-screen text)"
                                   : `${placement || "Top"} — ${sourceLabel(sources.placement)}`}
                               </dd>
                             </div>
                             <div>
                               <dt className="inline font-medium text-foreground">Alignment: </dt>
                               <dd className="inline">
                                 {item.exactText === undefined
                                   ? "Not applied (no on-screen text)"
                                   : `${item.textStyle?.align || item.textAlign || "center"} — ${sourceLabel(sources.alignment)}`}
                               </dd>
                             </div>
                             {item.textStyle ? (
                               <div>
                                 <dt className="font-medium text-foreground">Resolved text style:</dt>
                                 <dd>
                                   <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 text-[11px]" data-testid={`batch-variant-text-style-${index}`}>
                                     {JSON.stringify(previewTextStyle, null, 2)}
                                   </pre>
                                 </dd>
                               </div>
                             ) : null}
                              <div>
                                <dt className="font-medium text-foreground">Resolved entry JSON:</dt>
                                <dd>
                                  <pre
                                    className="mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]"
                                    data-testid={`batch-resolved-item-json-${index}`}
                                  >
                                    {JSON.stringify(resolvedEntryPreview, null, 2)}
                                  </pre>
                                </dd>
                              </div>
                           </dl>
                         </details>
                       );
                     })}
                  </div>
                </div>
              )}
              <details className="rounded-md bg-muted/40 p-3 text-xs">
                <summary className="cursor-pointer font-medium">JSON example and textStyle units</summary>
                 <p className="mt-2 text-muted-foreground">
                    {typeSplitEnabled
                      ? "Each item or top-level defaults may specify promptType and promotedItemType. A trusted referenceUploadId is always required. Product needs productId; Offer needs offerId; None needs neither. Persona and model are optional together."
                      : VIDEO_REFERENCE_BATCH_UPLOADER_EXPLANATION} Limits: 1–{VIDEO_REFERENCE_BATCH_MAX_ITEMS} items; <code>variant</code> and <code>exactText</code> are each at most {VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS} characters.
                </p>
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded bg-background p-3" data-testid="text-style-json-example">
                  {JSON.stringify(VIDEO_REFERENCE_BATCH_WORKED_EXAMPLE, null, 2)}
                </pre>
              </details>
            </div>
            </div>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-violet-200 bg-violet-50/70 shadow-sm dark:border-violet-900/60 dark:bg-violet-950/20" data-testid="card-generate-reference-ad">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-2xl text-xs text-muted-foreground">
             {configurationMode === "manual"
               ? typeSplitEnabled
                 ? "We study the reference's script, pacing, camera work, and style — then create an original video for your selected promotion."
                 : "We study the reference's script, pacing, camera work, and style — then create an original ad for your product, not a copy."
              : "Generate only the independently resolved videos represented by the uploaded JSON file."}
          </p>
          <Button
            onClick={() => configurationMode === "json"
              ? createBatch.mutate()
              : createRun.mutate()}
            disabled={!canGenerate}
            className="shrink-0 bg-violet-600 text-white hover:bg-violet-700"
            data-testid="button-generate"
          >
            {createRun.isPending || createBatch.isPending ? (
              <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Starting…</>
            ) : (
              <><Sparkles className="mr-1 h-4 w-4" /> {
                configurationMode === "json" && batchVariants.length > 0
                  ? `Generate ${batchVariants.length} videos`
                  : "Generate"
              }</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Final videos and in-progress runs */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight">Videos</h2>
        {runsLoading ? (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : runs.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground" data-testid="text-no-runs">
              No runs yet — upload a reference video and pick a product to get started.
            </CardContent>
          </Card>
        ) : (
          <>
            {groupedRuns.active.length > 0 ? (
              <Card className="p-3" data-testid="video-reference-run-queue">
                <div className="mb-2 flex items-center gap-2">
                  <ListOrdered className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="text-sm font-semibold">Run queue</span>
                  <span className="text-xs text-muted-foreground">
                    {groupedRuns.active.length} active {groupedRuns.active.length === 1 ? "run" : "runs"}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {groupedRuns.active.map((group, index) => {
                    const targetRun = group.runs.find(isRunViewActive) || group.runs[0];
                    const viewing = group.runs.some((run) => highlightedRunIds.has(run.id));
                    return (
                      <div
                        key={group.id}
                        className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${
                          viewing ? "border-primary/50 bg-primary/5" : "border-border"
                        }`}
                        data-testid={`video-reference-queue-entry-${index}`}
                      >
                        <Badge variant="secondary" className="shrink-0">
                          {group.kind === "batch" ? `Batch ${index + 1}` : `Run ${index + 1}`}
                        </Badge>
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                          {queueGroupSummary(group)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          aria-label={`View ${runGroupTitle(group)}`}
                          onClick={() => {
                            setHighlightedRunIds(new Set(group.runs.map((run) => run.id)));
                            window.setTimeout(() => {
                              document.getElementById(`video-reference-run-${targetRun.id}`)
                                ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
                            }, 0);
                          }}
                          data-testid={`button-view-video-reference-queue-${index}`}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                          View
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ) : null}

            {runsToRender.length > 0 ? (
          <div
            className={
              runsInMainGrid.length > 0
                ? "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                : "contents"
            }
            data-testid="grid-video-results"
            data-history-expanded-id={expandedHistoryId || undefined}
          >
            {runsToRender.map((run) => {
              const meta = statusMeta(run);
              const Icon = meta.icon;
              const isActive = ACTIVE_STATUSES.has(run.status);
              const isEditRendering = Boolean(
                run.videoUrl &&
                (run.quickEditInProgress || run.status === "adding_text"),
              );
              const isSaved = run.isSaved || savedRunIds.has(run.id);
              const savingThisRun =
                saveRun.isPending && saveRun.variables === run.id;
              const isExpandedHistoryRun =
                !legacyRunsResponse &&
                expandedHistoryGroup?.runs.some((historyRun) => historyRun.id === run.id);
              const renderRunCard = (element: ReactNode) => {
                if (!isExpandedHistoryRun) return element;
                if (!historyResultsTarget) return null;
                return createPortal(element, historyResultsTarget, run.id);
              };
              if (textEditTarget?.id === run.id) {
                const canEdit = run.quickEditAvailable ?? run.textEditAvailable;
                if (!canEdit) {
                  return renderRunCard(
                    <Card
                      key={run.id}
                      className="col-span-full border-border/60"
                      data-testid={`quick-edit-unavailable-${run.id}`}
                    >
                      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                        <div>
                          <h3 className="font-semibold">Quick edit unavailable</h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {run.quickEditUnavailableReason ||
                              run.textEditUnavailableReason ||
                              "This saved video does not have the durable media needed for a safe edit."}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setTextEditTarget(null)}
                        >
                          Back to video
                        </Button>
                      </CardContent>
                    </Card>
                  );
                }
                const duration = Math.max(
                  0.25,
                  Number(run.quickEditTimeline?.durationSec || run.referenceDurationSec || 0),
                );
                const editSourceUrl = run.quickEditSourceUrl || undefined;
                const editIsRendering = !!run.quickEditInProgress;
                const isBusy =
                  quickEdit.isPending ||
                  regenerateText.isPending ||
                  editIsRendering;
                const selectEditorLayer = (
                  layer: "video" | "text" | "audio",
                  focusText = false,
                ) => {
                  setSelectedEditorLayer(layer);
                  if (focusText) {
                    window.setTimeout(() => textEditInputRef.current?.focus(), 0);
                  }
                };
                const sourceStyle = textEditTarget.textStyleJson as Record<string, unknown> | null;
                const savedPlacement = sourceStyle
                  ? inferVideoReferenceTextPlacement(sourceStyle as any)
                  : "top";
                const placementChanged = textEditPlacement !== savedPlacement;
                // Every quick-edit request sends textPosition, and the API
                // persists that request with a middle-center anchor. Render
                // the same submitted position/anchor instead of the prior
                // snapshot so the editable layer matches the resulting MP4.
                const submittedTextPosition = resolveQuickEditTextPosition(
                  textEditPosition,
                  textEditPlacement,
                  sourceStyle,
                  textEditPositionWasDragged,
                );
                const sourcePosition = sourceStyle?.position as { x?: number; y?: number } | undefined;
                const sourceColor = typeof sourceStyle?.color === "string" ? sourceStyle.color.toUpperCase() : "#FFFFFF";
                const sourceWeight = Number(sourceStyle?.fontWeight);
                const sourceAlign = sourceStyle?.align;
                const positionChanged =
                  Math.abs(textEditPosition.x - Number(sourcePosition?.x ?? 0.5)) > 0.001 ||
                  Math.abs(textEditPosition.y - Number(sourcePosition?.y ?? 0.14)) > 0.001;
                const textTimingChanged =
                  quickTextStart > 0.001 ||
                  Math.abs(quickTextEnd - duration) > 0.001;
                const textStyleChanged =
                  quickTextColor.toUpperCase() !== sourceColor ||
                  quickTextWeight !== (Number.isFinite(sourceWeight) ? sourceWeight : 700) ||
                  quickTextAlign !== (sourceAlign === "left" || sourceAlign === "right" ? sourceAlign : "center");
                const quickOnly =
                  quickTrimStart > 0.001 ||
                  Math.abs(quickTrimEnd - duration) > 0.001 ||
                  quickAudio === "mute" ||
                  quickAudio === "replace" ||
                  positionChanged ||
                  placementChanged ||
                  textTimingChanged ||
                  textStyleChanged ||
                  !!(textEditTarget.quickEditRecipeJson as any)?.version ||
                  !textEditValue.trim();
                const apply = () => {
                  if (quickOnly) quickEdit.mutate();
                  else regenerateText.mutate();
                };
                return renderRunCard(
                  <Card
                    key={run.id}
                    className="col-span-full overflow-hidden border-primary/40 shadow-sm"
                    data-testid={`inline-quick-editor-${run.id}`}
                  >
                    <CardContent className="grid gap-5 p-4 lg:grid-cols-[minmax(280px,0.9fr)_minmax(360px,1.1fr)]">
                      <div className="space-y-3">
                        <div
                          ref={quickPreviewContainerRef}
                          className="relative aspect-[4/5] overflow-hidden rounded-md bg-black"
                          onPointerMove={(event) => {
                            if (!isDraggingText || !quickPreviewContentRect) return;
                            const rect = event.currentTarget.getBoundingClientRect();
                            setTextEditPosition(
                              clampQuickEditTextPosition(
                                videoReferencePreviewPointToCanvasPosition(
                                  {
                                    x: event.clientX - rect.left,
                                    y: event.clientY - rect.top,
                                  },
                                  quickPreviewContentRect,
                                ),
                              ),
                            );
                            setTextEditPositionWasDragged(true);
                          }}
                          onPointerUp={() => setIsDraggingText(false)}
                          onPointerCancel={() => setIsDraggingText(false)}
                        >
                          {editSourceUrl ? (
                            <video
                              ref={quickEditVideoRef}
                              src={editSourceUrl}
                              className="h-full w-full object-contain"
                              controls
                              playsInline
                              preload="metadata"
                              onLoadedMetadata={(event) => {
                                updateQuickPreviewGeometry();
                                event.currentTarget.currentTime =
                                  quickEditSourceOffset + quickTrimStart;
                                setQuickPlayhead(quickTrimStart);
                              }}
                              onTimeUpdate={(event) => {
                                const localTime = event.currentTarget.currentTime - quickEditSourceOffset;
                                const boundedTime = Math.max(
                                  quickTrimStart,
                                  Math.min(quickTrimEnd, localTime),
                                );
                                if (Math.abs(boundedTime - localTime) > 0.001) {
                                  event.currentTarget.pause();
                                  event.currentTarget.currentTime =
                                    quickEditSourceOffset + boundedTime;
                                  setQuickPlayhead(boundedTime);
                                  return;
                                }
                                setQuickPlayhead(boundedTime);
                              }}
                              onSeeking={(event) => {
                                const localTime = event.currentTarget.currentTime - quickEditSourceOffset;
                                const boundedTime = Math.max(
                                  quickTrimStart,
                                  Math.min(quickTrimEnd, localTime),
                                );
                                if (Math.abs(boundedTime - localTime) > 0.001) {
                                  event.currentTarget.currentTime =
                                    quickEditSourceOffset + boundedTime;
                                }
                                setQuickPlayhead(boundedTime);
                              }}
                              onPlay={() => setQuickIsPlaying(true)}
                              onPause={() => setQuickIsPlaying(false)}
                              data-testid={`video-quick-preview-${run.id}`}
                            />
                          ) : (
                            <div
                              className="flex h-full items-center justify-center px-6 text-center text-sm text-white/80"
                              data-testid={`quick-edit-source-unavailable-${run.id}`}
                            >
                              This video cannot be edited because its immutable clean source is unavailable.
                            </div>
                          )}
                          {editSourceUrl &&
                          textEditValue.trim() &&
                          quickPlayhead >= quickTextStart &&
                          quickPlayhead <= quickTextEnd ? (
                            (() => {
                              const metrics = quickPreviewContentRect
                                ? videoReferenceCanvasTextMetrics(
                                    textEditFontSize,
                                    textEditMaxWidthPercent / 100,
                                    quickPreviewContentRect,
                                  )
                                : null;
                              const previewPosition = quickPreviewContentRect
                                ? videoReferenceCanvasPositionToPreview(
                                    quickOnly
                                      ? submittedTextPosition
                                      : {
                                          x: 0.5,
                                          y: QUICK_EDIT_POSITION_BY_PLACEMENT[textEditPlacement],
                                        },
                                    quickPreviewContentRect,
                                  )
                                : null;
                              const previewTransform = quickOnly
                                ? "translate(-50%, -50%)"
                                : textEditPlacement === "top"
                                  ? "translate(-50%, 0)"
                                  : textEditPlacement === "bottom"
                                    ? "translate(-50%, -100%)"
                                    : "translate(-50%, -50%)";
                              return (
                            <button
                              ref={quickEditTextRef}
                              type="button"
                              className={`absolute z-10 cursor-move select-none rounded border px-2 py-1 text-center shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                                selectedEditorLayer === "text"
                                  ? "border-primary bg-black/60 ring-2 ring-primary/70"
                                  : "border-white/75 bg-black/45"
                              }`}
                              style={{
                                left: `${previewPosition?.x ?? 0}px`,
                                top: `${previewPosition?.y ?? 0}px`,
                                width: `${metrics?.maxWidthPx ?? 0}px`,
                                 boxSizing: "border-box",
                                transform: previewTransform,
                                fontSize: `${metrics?.fontSizePx ?? 0}px`,
                                fontFamily: textEditSpecimenAsset
                                  ? `"video-ref-picker-${textEditSpecimenAsset.id}", sans-serif`
                                  : "Inter, sans-serif",
                                 color: quickTextColor,
                                 fontWeight: quickTextWeight,
                                 textAlign: quickTextAlign,
                                 lineHeight: String(
                                   typeof sourceStyle?.lineHeight === "number"
                                     ? sourceStyle.lineHeight
                                     : 1.15,
                                 ),
                                 letterSpacing: `${
                                   (typeof sourceStyle?.letterSpacing === "number"
                                     ? sourceStyle.letterSpacing
                                     : 0) * (quickPreviewContentRect?.scale ?? 0)
                                 }px`,
                                 visibility: quickPreviewContentRect ? "visible" : "hidden",
                              }}
                              onPointerDown={(event) => {
                                event.preventDefault();
                                event.currentTarget.setPointerCapture(event.pointerId);
                                selectEditorLayer("text");
                                setIsDraggingText(true);
                              }}
                              onClick={() => selectEditorLayer("text", true)}
                              onPointerUp={() => setIsDraggingText(false)}
                              aria-label="Drag text block"
                              data-testid="button-drag-video-text"
                            >
                              {textEditValue}
                            </button>
                              );
                            })()
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          This preview uses the immutable clean/silent source, so editable text is composited only once on export. AI lettering already baked into that source cannot be removed.
                        </p>
                        <div
                          className="rounded-lg border bg-muted/20 p-3"
                          data-testid={`timeline-editor-${run.id}`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-semibold">Timeline</p>
                              <p className="text-[11px] text-muted-foreground">
                                {formatTimelineTime(quickPlayhead)} / {formatTimelineTime(duration)}
                              </p>
                            </div>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              aria-label={quickIsPlaying ? "Pause preview" : "Play preview"}
                              onClick={() => {
                                const video = quickEditVideoRef.current;
                                if (!video) return;
                                 if (video.paused) {
                                   const localTime = video.currentTime - quickEditSourceOffset;
                                   if (localTime < quickTrimStart || localTime >= quickTrimEnd) {
                                     video.currentTime =
                                       quickEditSourceOffset + quickTrimStart;
                                   }
                                   void video.play();
                                 }
                                else video.pause();
                              }}
                              disabled={isBusy}
                              data-testid="button-timeline-play"
                            >
                              {quickIsPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                          <div
                            className="relative h-28 select-none rounded-md border bg-background p-2"
                            role="group"
                            aria-label="Video editor timeline"
                            onPointerMove={(event) => {
                              if (!timelineTrimDrag) return;
                              const rect = timelineVideoLaneRef.current?.getBoundingClientRect();
                              if (!rect?.width) return;
                              const next = Math.max(
                                0,
                                Math.min(duration, ((event.clientX - rect.left) / rect.width) * duration),
                              );
                              if (timelineTrimDrag === "start") setQuickTrimStartBounded(next, duration);
                              else setQuickTrimEndBounded(next, duration);
                            }}
                            onPointerUp={() => setTimelineTrimDrag(null)}
                            onPointerCancel={() => setTimelineTrimDrag(null)}
                            data-testid="timeline-scrubber"
                          >
                            <div className="absolute inset-x-2 top-2 flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              <span className="w-12 shrink-0">Track</span>
                              <span className="flex-1">Source video</span>
                            </div>
                            <div className="absolute inset-x-2 top-8 flex items-center gap-2">
                              <span className="w-12 shrink-0 text-[10px] font-medium text-muted-foreground">Video</span>
                              <div
                                ref={timelineVideoLaneRef}
                                className={`relative h-5 flex-1 rounded ${
                                  selectedEditorLayer === "video"
                                    ? "bg-primary/30 ring-2 ring-primary"
                                    : "bg-primary/15"
                                }`}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selectedEditorLayer === "video"}
                                aria-label="Select video layer"
                                onClick={(event) => {
                                  selectEditorLayer("video");
                                  const rect = event.currentTarget.getBoundingClientRect();
                                  if (!rect.width) return;
                                  const next = Math.max(
                                    0,
                                    Math.min(duration, ((event.clientX - rect.left) / rect.width) * duration),
                                  );
                                  setQuickPlayhead(next);
                                  if (quickEditVideoRef.current) {
                                    quickEditVideoRef.current.currentTime =
                                      quickEditSourceOffset + next;
                                  }
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    selectEditorLayer("video");
                                  }
                                }}
                                data-testid="timeline-video-lane"
                              >
                                <div className="absolute inset-0 overflow-hidden rounded">
                                  <div
                                    className="absolute inset-y-0 rounded bg-primary/35"
                                    style={{
                                      left: `${(quickTrimStart / duration) * 100}%`,
                                      width: `${((quickTrimEnd - quickTrimStart) / duration) * 100}%`,
                                    }}
                                  />
                                </div>
                                {(["start", "end"] as const).map((edge) => {
                                  const at = edge === "start" ? quickTrimStart : quickTrimEnd;
                                  return (
                                    <button
                                      key={edge}
                                      type="button"
                                      className="absolute -top-1 z-20 h-7 w-2 rounded bg-primary shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                      style={{ left: `calc(${(at / duration) * 100}% - 4px)` }}
                                      aria-label={`Trim ${edge} handle`}
                                      onPointerDown={(event) => {
                                        event.stopPropagation();
                                        event.currentTarget.setPointerCapture(event.pointerId);
                                        setTimelineTrimDrag(edge);
                                      }}
                                      onClick={(event) => event.stopPropagation()}
                                      disabled={isBusy}
                                      onKeyDown={(event) => {
                                        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                                        event.preventDefault();
                                        const delta = event.key === "ArrowLeft" ? -0.1 : 0.1;
                                        if (edge === "start") setQuickTrimStartBounded(quickTrimStart + delta, duration);
                                        else setQuickTrimEndBounded(quickTrimEnd + delta, duration);
                                      }}
                                      data-testid={`timeline-trim-${edge}`}
                                    />
                                  );
                                })}
                                <div
                                  className="pointer-events-none absolute -top-1 z-10 h-7 w-px bg-red-500"
                                  style={{ left: `${(quickPlayhead / duration) * 100}%` }}
                                  data-testid="timeline-playhead"
                                />
                              </div>
                            </div>
                            <div className="absolute inset-x-2 top-[3.65rem] flex items-center gap-2">
                              <span className="w-12 shrink-0 text-[10px] font-medium text-muted-foreground">Text</span>
                              <div
                                className={`relative h-5 flex-1 rounded ${
                                  selectedEditorLayer === "text"
                                    ? "bg-violet-500/30 ring-2 ring-violet-500"
                                    : "bg-violet-500/15"
                                }`}
                              >
                                <button
                                  type="button"
                                  className={`absolute inset-y-0 rounded px-2 text-left text-[10px] font-medium focus:outline-none focus:ring-2 focus:ring-primary ${
                                    textEditValue.trim()
                                      ? "bg-violet-500/55 text-violet-950 dark:text-violet-50"
                                      : "text-muted-foreground"
                                  }`}
                                    style={{
                                      left: textEditValue.trim() ? `${(quickTextStart / duration) * 100}%` : "0%",
                                      width: textEditValue.trim()
                                        ? `${((quickTextEnd - quickTextStart) / duration) * 100}%`
                                        : "100%",
                                    }}
                                    onClick={() => selectEditorLayer("text", true)}
                                    aria-pressed={selectedEditorLayer === "text"}
                                    aria-label="Select text layer"
                                    data-testid="timeline-text-layer"
                                  >
                                    {textEditValue.trim().slice(0, 24) || "Add text layer"}
                                  </button>
                                <div
                                  className="pointer-events-none absolute -top-1 z-10 h-7 w-px bg-red-500"
                                  style={{ left: `${(quickPlayhead / duration) * 100}%` }}
                                />
                              </div>
                            </div>
                            <div className="absolute inset-x-2 top-[5.3rem] flex items-center gap-2">
                              <span className="w-12 shrink-0 text-[10px] font-medium text-muted-foreground">Audio</span>
                              <button
                                type="button"
                                className={`relative h-5 flex-1 rounded text-left ${
                                  selectedEditorLayer === "audio"
                                    ? "bg-emerald-500/45 ring-2 ring-emerald-500"
                                    : quickAudio === "mute" ? "bg-muted" : "bg-emerald-500/30"
                                }`}
                                aria-pressed={selectedEditorLayer === "audio"}
                                aria-label="Select audio layer"
                                onClick={() => selectEditorLayer("audio")}
                                data-testid="timeline-audio-lane"
                              >
                                <span className="px-2 text-[10px] leading-5 text-muted-foreground">
                                  {quickAudio === "mute"
                                    ? "Muted"
                                    : quickAudio === "replace"
                                      ? selectedQuickMusicAsset?.label || "Selected track"
                                      : "Soundtrack"}
                                </span>
                                <div
                                  className="pointer-events-none absolute -top-1 z-10 h-7 w-px bg-red-500"
                                  style={{ left: `${(quickPlayhead / duration) * 100}%` }}
                                />
                              </button>
                            </div>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={duration}
                            step={0.1}
                            value={quickPlayhead}
                            onChange={(event) => {
                              const next = Number(event.target.value);
                              setQuickPlayhead(next);
                              if (quickEditVideoRef.current) {
                                quickEditVideoRef.current.currentTime =
                                  quickEditSourceOffset + next;
                              }
                            }}
                            aria-label="Scrub video timeline"
                            className="mt-2 w-full accent-primary"
                            disabled={isBusy}
                            data-testid="input-timeline-scrub"
                          />
                          <div className="mt-2 flex justify-between pl-14 text-[10px] text-muted-foreground">
                            <span>0:00</span>
                            <span>{formatTimelineTime(duration / 2)}</span>
                            <span>{formatTimelineTime(duration)}</span>
                          </div>
                        </div>
                        {run.quickEditInProgress ? (
                          <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Rendering your edit from the clean source. This video remains the last completed version.
                          </p>
                        ) : null}
                        {run.quickEditError ? (
                          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            The last quick edit could not finish: {run.quickEditError}
                          </p>
                        ) : null}
                      </div>
                      <div
                        className={`space-y-4 rounded-lg border p-3 ${
                          selectedEditorLayer === "text"
                            ? "border-violet-500/60 bg-violet-500/[0.03]"
                            : "border-transparent"
                        }`}
                        data-testid="editor-properties-panel"
                      >
                        <div>
                          <h3 className="font-semibold">
                            {typeof textEditTarget.exactText === "string" ? "Edit and regenerate text" : "Add text to video"}
                          </h3>
                          <p className="mt-1 text-xs font-medium uppercase tracking-wide text-violet-600 dark:text-violet-300">
                            {selectedEditorLayer} layer selected
                          </p>
                          <p className="text-sm text-muted-foreground">
                             Edit the clean source timeline. The exported MP4 is rendered server-side without stacking edits.
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium" htmlFor={`quick-exact-text-${run.id}`}>Exact on-video text</label>
                          <Textarea
                            ref={textEditInputRef}
                            id={`quick-exact-text-${run.id}`}
                            value={textEditValue}
                            onChange={(event) => setTextEditValue(event.target.value)}
                            onFocus={() => selectEditorLayer("text")}
                            rows={3}
                            maxLength={500}
                            disabled={isBusy}
                            data-testid="input-regenerate-exact-text"
                          />
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-text-start-${run.id}`}>
                              Text starts · {quickTextStart.toFixed(1)}s
                            </label>
                            <input
                              id={`quick-text-start-${run.id}`}
                              type="range"
                              min={quickTrimStart}
                              max={Math.max(quickTrimStart, quickTextEnd - 0.25)}
                              step="any"
                              value={quickTextStart}
                              onChange={(event) => setQuickTextStart(Number(event.target.value))}
                              onFocus={() => selectEditorLayer("text")}
                              className="w-full accent-violet-500"
                              disabled={isBusy || !textEditValue.trim()}
                              data-testid="input-quick-text-start"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-text-end-${run.id}`}>
                              Text ends · {quickTextEnd.toFixed(1)}s
                            </label>
                            <input
                              id={`quick-text-end-${run.id}`}
                              type="range"
                              min={Math.min(quickTrimEnd, quickTextStart + 0.25)}
                              max={quickTrimEnd}
                              step="any"
                              value={quickTextEnd}
                              onChange={(event) => setQuickTextEnd(Number(event.target.value))}
                              onFocus={() => selectEditorLayer("text")}
                              className="w-full accent-violet-500"
                              disabled={isBusy || !textEditValue.trim()}
                              data-testid="input-quick-text-end"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-trim-start-${run.id}`}>Trim start · {quickTrimStart.toFixed(1)}s</label>
                            <input
                              id={`quick-trim-start-${run.id}`}
                              type="range" min={0} max={Math.max(0, quickTrimEnd - 0.25)}
                              step="any" value={quickTrimStart}
                               onChange={(event) => setQuickTrimStartBounded(Number(event.target.value), duration)}
                              onFocus={() => selectEditorLayer("video")}
                              className="w-full accent-primary" disabled={isBusy}
                              data-testid="input-quick-trim-start"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-placement-${run.id}`}>Vertical placement</label>
                            <select
                              id={`quick-placement-${run.id}`}
                              value={textEditPlacement}
                              onChange={(event) => {
                                setTextEditPlacement(event.target.value as VideoReferenceTextPlacement);
                                setTextEditPositionWasDragged(false);
                              }}
                              disabled={isBusy}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                              data-testid="select-regenerate-text-placement"
                            >
                              <option value="top">Top</option>
                              <option value="upper-third">Upper third</option>
                              <option value="middle">Middle</option>
                              <option value="lower-third">Lower third</option>
                              <option value="bottom">Bottom</option>
                            </select>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-trim-end-${run.id}`}>Trim end · {quickTrimEnd.toFixed(1)}s</label>
                            <input
                              id={`quick-trim-end-${run.id}`}
                              type="range" min={Math.min(duration, quickTrimStart + 0.25)} max={duration}
                              step="any" value={quickTrimEnd}
                               onChange={(event) => setQuickTrimEndBounded(Number(event.target.value), duration)}
                              onFocus={() => selectEditorLayer("video")}
                              className="w-full accent-primary" disabled={isBusy}
                              data-testid="input-quick-trim-end"
                            />
                          </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-text-color-${run.id}`}>Color</label>
                            <input
                              id={`quick-text-color-${run.id}`}
                              type="color"
                              value={quickTextColor}
                              onChange={(event) => setQuickTextColor(event.target.value.toUpperCase())}
                              disabled={isBusy || !textEditValue.trim()}
                              className="h-9 w-full rounded-md border border-input bg-background px-1"
                              data-testid="input-quick-text-color"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-text-weight-${run.id}`}>Weight</label>
                            <select
                              id={`quick-text-weight-${run.id}`}
                              value={quickTextWeight}
                              onChange={(event) => setQuickTextWeight(Number(event.target.value))}
                              disabled={isBusy || !textEditValue.trim()}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                              data-testid="select-quick-text-weight"
                            >
                              {[400, 500, 600, 700, 800, 900].map((weight) => <option key={weight} value={weight}>{weight}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-text-align-${run.id}`}>Alignment</label>
                            <select
                              id={`quick-text-align-${run.id}`}
                              value={quickTextAlign}
                              onChange={(event) => setQuickTextAlign(event.target.value as VideoReferenceTextAlign)}
                              disabled={isBusy || !textEditValue.trim()}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                              data-testid="select-quick-text-align"
                            >
                              <option value="left">Left</option>
                              <option value="center">Center</option>
                              <option value="right">Right</option>
                            </select>
                          </div>
                        </div>
                        <div className="space-y-3 rounded-md border p-3" onFocus={() => selectEditorLayer("audio")}>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-audio-${run.id}`}>Soundtrack</label>
                            <select
                              id={`quick-audio-${run.id}`}
                              value={quickAudio}
                              onChange={(event) => setQuickAudio(event.target.value as "keep" | "mute" | "replace")}
                              disabled={isBusy || referenceAssetsLoading}
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                              data-testid="select-quick-audio"
                            >
                              <option value="keep">Keep current soundtrack</option>
                              <option value="mute">Mute</option>
                              <option value="replace">Select a track</option>
                            </select>
                            <p className="text-xs text-muted-foreground">
                              {quickAudio === "keep"
                                ? "Reuse the current approved soundtrack."
                                : quickAudio === "mute"
                                  ? "Export a deliberately silent MP4."
                                  : "Replace the soundtrack with an active library track."}
                            </p>
                          </div>
                          {quickAudio === "replace" ? (
                            <div className="grid gap-3 sm:grid-cols-2">
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium" htmlFor={`quick-music-${run.id}`}>Track</label>
                                <select
                                  id={`quick-music-${run.id}`}
                                  value={quickMusicAssetId}
                                  onChange={(event) => setQuickMusicAssetId(event.target.value)}
                                  disabled={isBusy || referenceAssetsLoading}
                                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                                  data-testid="select-quick-music-asset"
                                >
                                  <option value="">Choose an active track</option>
                                  {musicAssets.map((asset) => (
                                    <option key={asset.id} value={asset.id}>
                                      {asset.label}{asset.durationSec ? ` (${Math.round(asset.durationSec)}s)` : ""}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium">Upload track</label>
                                <input
                                  ref={quickMusicAssetInputRef}
                                  type="file"
                                  accept=".mp3,.wav,audio/mpeg,audio/wav"
                                  className="hidden"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) void handleAssetUpload("music", file, "quick-edit");
                                  }}
                                  data-testid="input-quick-upload-music"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => quickMusicAssetInputRef.current?.click()}
                                  disabled={isBusy || uploadingAssetKind !== null}
                                  data-testid="button-quick-upload-music"
                                >
                                  {uploadingAssetKind === "music"
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Upload className="mr-2 h-4 w-4" />}
                                  Upload MP3 or WAV
                                </Button>
                              </div>
                              {selectedQuickMusicAsset ? (
                                <audio
                                  controls
                                  preload="none"
                                  src={selectedQuickMusicAsset.previewUrl}
                                  className="h-9 w-full sm:col-span-2"
                                  aria-label={`Preview ${selectedQuickMusicAsset.label}`}
                                  data-testid="audio-quick-selected-music"
                                />
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium" htmlFor={`quick-font-${run.id}`}>Font</label>
                             <Select
                               value={pickerValueFor("text-edit", textEditFontChoice)}
                               onValueChange={(value) => {
                                 void handleFontPickerChange(value, "text-edit");
                               }}
                               onOpenChange={(open) => {
                                 if (open) {
                                   ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((font) => font.family));
                                 }
                               }}
                               disabled={
                                 isBusy ||
                                 referenceAssetsLoading ||
                                 pendingGoogleFont !== null
                               }
                             >
                                 <SelectTrigger
                                   id={`quick-font-${run.id}`}
                                   className="h-9 w-full"
                                   data-testid="select-regenerate-text-font"
                                   data-value={pickerValueFor("text-edit", textEditFontChoice)}
                                 >
                                 <SelectValue placeholder="Default (Inter)" />
                               </SelectTrigger>
                               <SelectContent className="max-h-72">
                                 {typeof textEditTarget.exactText === "string" ? (
                                   <SelectItem value={KEEP_CURRENT_FONT} data-value={KEEP_CURRENT_FONT}>
                                     Keep current ({runFontLabel(textEditTarget)})
                                   </SelectItem>
                                 ) : null}
                                 <SelectItem value={DEFAULT_FONT} data-value={DEFAULT_FONT}>
                                   Default (Inter)
                                 </SelectItem>
                                 <SelectSeparator />
                                 <VideoReferenceFontOptions fontAssets={fontAssets} />
                               </SelectContent>
                             </Select>
                            {pendingGoogleFont?.target === "text-edit" ? (
                              <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                                Preparing {pendingGoogleFont.family}…
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <p
                          className="rounded-md bg-muted/40 px-3 py-2 text-lg"
                          style={{
                            fontFamily: textEditSpecimenAsset
                              ? `"video-ref-picker-${textEditSpecimenAsset.id}", sans-serif`
                              : textEditFontChoice === KEEP_CURRENT_FONT && textEditTarget.textFontFamily
                                ? `"${textEditTarget.textFontFamily}", Inter, sans-serif`
                                : "Inter, sans-serif",
                              color: quickTextColor,
                              fontWeight: quickTextWeight,
                              textAlign: quickTextAlign,
                          }}
                          data-testid="text-regenerate-font-specimen"
                        >
                          Aa Bb Cc 0123 — {textEditValue.trim() || "Your exact text"}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium">Font size · {textEditFontSize}px</label>
                            <input type="range" min={12} max={240} value={textEditFontSize} onChange={(event) => setTextEditFontSize(Number(event.target.value))} className="w-full accent-primary" disabled={isBusy} data-testid="input-regenerate-text-font-size" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-medium">Text width · {textEditMaxWidthPercent}%</label>
                            <input type="range" min={10} max={100} value={textEditMaxWidthPercent} onChange={(event) => setTextEditMaxWidthPercent(Number(event.target.value))} className="w-full accent-primary" disabled={isBusy} data-testid="input-regenerate-text-max-width" />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t pt-3">
                          <Button type="button" variant="outline" onClick={() => setTextEditTarget(null)} disabled={isBusy || uploadingAssetKind !== null}>Cancel</Button>
                            <Button
                              type="button"
                              onClick={apply}
                              disabled={
                                isBusy ||
                                 !editSourceUrl ||
                                uploadingAssetKind !== null ||
                                (quickAudio === "replace" && !quickMusicAssetId) ||
                                quickTrimEnd - quickTrimStart < 0.25 ||
                                (!!textEditValue.trim() && (
                                  quickTextStart < quickTrimStart ||
                                  quickTextEnd > quickTrimEnd ||
                                  quickTextEnd - quickTextStart < 0.25
                                ))
                              }
                              data-testid="button-confirm-regenerate-text"
                            >
                            {isBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                            Apply quick edit
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }
              return renderRunCard(
                <Card
                  key={run.id}
                  id={`video-reference-run-${run.id}`}
                  tabIndex={-1}
                  className={`overflow-hidden transition ${
                    highlightedRunIds.has(run.id) ? "ring-2 ring-primary ring-offset-2" : ""
                  }`}
                  data-highlighted={highlightedRunIds.has(run.id) ? "true" : undefined}
                  data-testid={`card-run-${run.id}`}
                >
                  <div
                    className="relative flex aspect-[4/5] items-center justify-center bg-black"
                    data-testid={`media-run-${run.id}`}
                  >
                    {run.videoUrl ? (
                      <>
                        <video
                          key={run.videoUrl}
                          src={run.videoUrl}
                          className="absolute inset-0 h-full w-full object-contain"
                          controls
                          playsInline
                          preload="metadata"
                          data-testid={`video-result-${run.id}`}
                        />
                        <div
                          className="absolute right-3 top-3 z-10"
                          data-testid={`phone-preview-position-${run.id}`}
                        >
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            className="aspect-square shrink-0 rounded-full border border-white/15 bg-neutral-900/80 p-0 text-white shadow-lg backdrop-blur-sm hover:scale-105 hover:bg-neutral-900 hover:text-white"
                            style={{ width: 44, height: 44, minWidth: 44 }}
                            aria-label={`Open ${run.productName || "Product"} in phone preview`}
                            title="Open phone preview"
                            onClick={() => setPhonePreview({
                              videoUrl: run.videoUrl!,
                              displayName: run.productName || "Product",
                            })}
                            data-testid={`button-phone-preview-${run.id}`}
                          >
                            <ExternalLink className="h-5 w-5" aria-hidden="true" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-3 px-5 text-center text-white/70">
                        <Video className="h-8 w-8" aria-hidden="true" />
                        <span className="text-xs font-medium">Video preview will appear here</span>
                      </div>
                    )}
                    {isEditRendering ? (
                      <div
                        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/45 px-5 text-center text-white"
                        data-testid={`overlay-edit-rendering-${run.id}`}
                        role="status"
                        aria-live="polite"
                      >
                        <div className="flex max-w-[15rem] flex-col items-center gap-2 rounded-lg bg-black/70 px-4 py-3 shadow-lg backdrop-blur-sm">
                          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                          <span className="text-sm font-semibold">Regenerating video</span>
                          <span className="text-xs text-white/80">
                            Your previous version remains playable while the edit renders.
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <CardContent className="space-y-2.5 p-3.5">
                    <div
                      className="flex min-w-0 items-center justify-between gap-2"
                      data-testid={`summary-run-${run.id}`}
                    >
                      <Link
                        href={`/brands/${brandId}/products/${run.productId}`}
                        className="flex min-w-0 items-center gap-2"
                        data-testid={`tag-run-product-${run.id}`}
                        aria-label={`Product: ${run.productName || "Product"}`}
                      >
                        <RunSnapshotThumbnail url={run.productImageUrl} kind="product" />
                        <span className="truncate text-sm font-semibold" title={run.productName || "Product"}>
                          {run.productName || "Product"}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
                      </Link>
                      {run.quickEditInProgress ? (
                        <Badge variant="secondary" className="shrink-0 gap-1" data-testid={`badge-quick-edit-rendering-${run.id}`}>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Rendering edit
                        </Badge>
                      ) : (
                        <Badge variant={meta.variant} className="shrink-0 gap-1" data-testid={`badge-run-status-${run.id}`}>
                          <Icon className={`h-3 w-3 ${meta.spinning ? "animate-spin" : ""}`} />
                          {meta.label}
                        </Badge>
                      )}
                    </div>

                    {run.selectedVariantSnapshot?.name ||
                    (run.batchId && Number.isInteger(run.batchIndex)) ||
                    run.personaCharacterName ? (
                      <div
                        className="divide-y divide-border/60 border-y border-border/60"
                        data-testid={`metadata-run-${run.id}`}
                      >
                        {run.selectedVariantSnapshot?.name ? (
                          <div
                            className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2 py-2 text-xs"
                            data-testid={`tag-run-product-variant-${run.id}`}
                            aria-label={`Catalog variant: ${run.selectedVariantSnapshot.name}`}
                          >
                            <span className="text-muted-foreground">Catalog variant</span>
                            <span className="truncate text-right font-medium" title={run.selectedVariantSnapshot.name}>
                              {run.selectedVariantSnapshot.name}
                            </span>
                          </div>
                        ) : null}
                        {run.batchId && Number.isInteger(run.batchIndex) ? (
                          <div
                            className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2 py-2 text-xs"
                            data-testid={`tag-run-batch-index-${run.id}`}
                            aria-label={`Batch item ${Number(run.batchIndex) + 1}`}
                          >
                            <span className="text-muted-foreground">Batch item</span>
                            <span className="truncate text-right font-medium">{Number(run.batchIndex) + 1}</span>
                          </div>
                        ) : null}
                        {run.personaCharacterName ? (
                          <div
                            className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2 py-2 text-xs"
                            data-testid={`tag-run-model-${run.id}`}
                            aria-label={`Model: ${run.personaCharacterName}`}
                          >
                            <span className="text-muted-foreground">Model</span>
                            {run.personaId ? (
                              <Link
                                href={`/brands/${brandId}/personas/${run.personaId}`}
                                className="flex min-w-0 items-center justify-end gap-1.5 rounded-sm font-medium text-foreground underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                data-testid={`link-run-persona-${run.id}`}
                                aria-label={`View persona for ${run.personaCharacterName}`}
                              >
                                <RunSnapshotThumbnail url={run.personaCharacterAvatarUrl} kind="model" />
                                <span className="truncate" title={run.personaCharacterName}>
                                  {run.personaCharacterName}
                                </span>
                              </Link>
                            ) : (
                              <span className="flex min-w-0 items-center justify-end gap-1.5">
                                <RunSnapshotThumbnail url={run.personaCharacterAvatarUrl} kind="model" />
                                <span className="truncate font-medium" title={run.personaCharacterName}>
                                  {run.personaCharacterName}
                                </span>
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {run.derivedTransform === "crop_4_5_center" ? (
                      <p
                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                        data-testid={`label-derived-crop-${run.id}`}
                      >
                        <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        Separate centered 4:5 duplicate — the original video is retained.
                      </p>
                    ) : null}

                    {run.variant ||
                    typeof run.exactText === "string" ||
                    run.musicAssetId ||
                    (run.status === "ready" && !run.textEditAvailable && run.textEditUnavailableReason) ||
                    run.videoPromptProvenance !== undefined ||
                    run.videoPromptVersionNumber !== undefined ? (
                      <details className="rounded-md border border-border/60 px-2.5 py-2" data-testid={`details-run-${run.id}`}>
                        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                          Details
                        </summary>
                        <div className="mt-2 space-y-2">
                          <p className="text-xs text-muted-foreground" data-testid={`prompt-provenance-${run.id}`}>
                            <span className="font-medium text-foreground">Prompt source:</span>{" "}
                            {run.videoPromptProvenance?.source === "in_app" &&
                            typeof run.videoPromptProvenance.versionNumber === "number"
                              ? `Brand prompt v${run.videoPromptProvenance.versionNumber}`
                              : typeof run.videoPromptVersionNumber === "number"
                                ? `Brand prompt v${run.videoPromptVersionNumber}`
                              : "Legacy Sim workflow"}
                          </p>
                          {run.variant ? (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">Creative direction:</span>{" "}
                              “{run.variant}”
                            </p>
                          ) : null}

                          {typeof run.exactText === "string" ? (
                            <div data-testid={`text-run-exact-text-${run.id}`}>
                              <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                Exact on-video text
                              </p>
                              <p className="line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                                {run.exactText.length > 0 ? run.exactText : "(empty string)"}
                              </p>
                              <p className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground" data-testid={`badge-run-font-asset-${run.id}`}>
                                <Type className="h-3 w-3 shrink-0" aria-hidden="true" />
                                <span className="font-medium">{runFontLabel(run)}</span>
                              </p>
                            </div>
                          ) : null}

                          {run.musicAssetId ? (
                            <p className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid={`badge-run-music-asset-${run.id}`}>
                              <Music className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="font-medium">{run.musicAssetLabel || run.musicAssetId}</span>
                            </p>
                          ) : null}

                          {run.status === "ready" &&
                          !(run.quickEditAvailable ?? run.textEditAvailable) &&
                          (run.quickEditUnavailableReason || run.textEditUnavailableReason) ? (
                            <p
                              className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground"
                              data-testid={`text-edit-unavailable-${run.id}`}
                            >
                              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                              <span>{run.quickEditUnavailableReason || run.textEditUnavailableReason}</span>
                            </p>
                          ) : null}
                        </div>
                      </details>
                    ) : null}

                    {isActive ? (
                      <p className="text-xs text-muted-foreground" data-testid={`text-run-progress-${run.id}`}>
                        {run.status === "queued"
                          ? "Waiting for an available generation slot…"
                          : run.status === "preparing_model"
                          ? "Creating an original product-wearing model reference…"
                            : run.status === "cropping"
                              ? "Center-cropping the selected final MP4 to exact 4:5. The original is retained."
                            : run.status === "rendering"
                            ? `Generating a ~${run.durationSec ?? Math.round(run.referenceDurationSec)}s video with Seedance…`
                            : run.status === "adding_text"
                              ? "Adding your exact copy…"
                            : run.status === "adding_music"
                              ? "Adding original background music…"
                            : "Studying the reference video…"}
                      </p>
                    ) : null}

                    {run.status === "failed" ? (
                      <div
                        className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                        data-testid={`text-run-error-${run.id}`}
                        role="alert"
                      >
                        <p className="font-medium">This run could not finish.</p>
                        <p className="mt-0.5">
                          {run.error || "Something went wrong."}
                          {run.derivedFromRunId
                            ? " The original video is retained."
                            : run.videoUrl
                              ? " Your previous video is still available."
                              : ""}
                        </p>
                      </div>
                    ) : null}

                    {run.quickEditInProgress ? (
                      <p
                        className="flex items-start gap-1.5 text-xs text-muted-foreground"
                        data-testid={`text-quick-edit-rendering-${run.id}`}
                      >
                        <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
                        <span>Rendering your edit from the clean source. This video remains playable until the new MP4 is ready.</span>
                      </p>
                    ) : null}

                    {run.quickEditError ? (
                      <p
                        className="flex items-start gap-1.5 text-xs text-destructive"
                        data-testid={`text-quick-edit-error-${run.id}`}
                      >
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span>The last quick edit could not finish: {run.quickEditError} Your previous video is still available.</span>
                      </p>
                    ) : null}

                    {run.status === "ready" && run.videoUrl && !run.derivedFromRunId ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => duplicateCrop.mutate(run.id)}
                        disabled={duplicateCrop.isPending || derivedCropSourceIds.has(run.id)}
                        className="h-9 w-full justify-center"
                        title="Creates a separate centered 4:5 MP4. The original is retained."
                        data-layout="full-width"
                        data-testid={`button-duplicate-crop-${run.id}`}
                      >
                        {duplicateCrop.isPending && duplicateCrop.variables === run.id ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Copy className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {derivedCropSourceIds.has(run.id)
                          ? "4:5 duplicate created"
                          : "Duplicate & crop to 4:5"}
                      </Button>
                    ) : null}

                    <div className="flex flex-wrap items-center gap-1.5 border-t pt-2">
                      <span className="mr-auto inline-flex max-w-full items-center gap-1 truncate text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {formatWhen(run.createdAt)}
                      </span>
                      <div className="flex items-center gap-1">
                        {run.status === "failed" &&
                        run.derivedTransform === "crop_4_5_center" &&
                        run.derivedFromRunId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => duplicateCrop.mutate(run.derivedFromRunId!)}
                            disabled={duplicateCrop.isPending}
                            className="h-8"
                            data-testid={`button-retry-crop-${run.id}`}
                          >
                            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${duplicateCrop.isPending ? "animate-spin" : ""}`} />
                            Retry crop
                          </Button>
                        ) : null}
                        {run.status === "failed" && !run.derivedFromRunId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => retryRun.mutate(run.id)}
                            disabled={retryRun.isPending && retryRun.variables === run.id}
                            className="h-8"
                            data-testid={`button-retry-${run.id}`}
                          >
                            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${
                              retryRun.isPending && retryRun.variables === run.id ? "animate-spin" : ""
                            }`} />
                            Retry
                          </Button>
                        ) : null}
                        {run.status === "ready" && run.videoUrl ? (
                          <>
                           {run.quickEditAvailable ?? run.textEditAvailable ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const textStyle = run.textStyleJson as Record<string, unknown> | null;
                                const savedFontSize = Number(textStyle?.fontSize);
                                const savedMaxWidth = Number(textStyle?.maxWidth);
                                setTextEditTarget(run);
                                setSelectedEditorLayer("text");
                                quickEdit.reset();
                                setTextEditValue(
                                  typeof run.exactText === "string"
                                    ? run.exactText
                                    : "",
                                );
                                setTextEditPlacement(
                                  run.textStyleJson
                                    ? inferVideoReferenceTextPlacement(
                                        run.textStyleJson as any,
                                      )
                                    : "top",
                                );
                                setTextEditFontSize(
                                  Number.isFinite(savedFontSize) &&
                                  savedFontSize >= 12 &&
                                  savedFontSize <= 240
                                    ? savedFontSize
                                    : DEFAULT_EXACT_TEXT_FONT_SIZE,
                                );
                                setTextEditMaxWidthPercent(
                                  Number.isFinite(savedMaxWidth) &&
                                  savedMaxWidth >= 0.1 &&
                                  savedMaxWidth <= 1
                                    ? Math.round(savedMaxWidth * 100)
                                    : DEFAULT_EXACT_TEXT_MAX_WIDTH_PERCENT,
                                );
                                setTextEditFontChoice(
                                  typeof run.exactText === "string"
                                    ? KEEP_CURRENT_FONT
                                    : DEFAULT_FONT,
                                );
                                const position = textStyle?.position as
                                  | { x?: number; y?: number }
                                  | undefined;
                                setTextEditPosition({
                                  x: typeof position?.x === "number" ? position.x : 0.5,
                                  y: typeof position?.y === "number" ? position.y : 0.14,
                                });
                                setTextEditPositionWasDragged(false);
                                setQuickTextColor(
                                  typeof textStyle?.color === "string" && /^#[0-9a-f]{6}$/i.test(textStyle.color)
                                    ? textStyle.color.toUpperCase()
                                    : "#FFFFFF",
                                );
                                const savedWeight = Number(textStyle?.fontWeight);
                                setQuickTextWeight(
                                  Number.isInteger(savedWeight) && savedWeight >= 100 && savedWeight <= 900
                                    ? savedWeight
                                    : 700,
                                );
                                setQuickTextAlign(
                                  textStyle?.align === "left" || textStyle?.align === "right"
                                    ? textStyle.align
                                    : "center",
                                );
                                const priorRecipe = run.quickEditRecipeJson as {
                                  version?: unknown;
                                  textStartSec?: unknown;
                                  textEndSec?: unknown;
                                  audio?: unknown;
                                  audioAssetId?: unknown;
                                } | null;
                                const timeline = run.quickEditTimeline;
                                const sourceStartSec = Number(timeline?.sourceStartSec);
                                const sourceEndSec = Number(timeline?.sourceEndSec);
                                const visibleDuration = Math.max(
                                  0.25,
                                  Number(
                                    timeline?.durationSec ||
                                    run.referenceDurationSec ||
                                    0.25
                                  ),
                                );
                                const hasPriorRecipe = priorRecipe?.version === 1;
                                setQuickEditSourceOffset(
                                  Number.isFinite(sourceStartSec) ? sourceStartSec : 0,
                                );
                                setQuickTrimStart(0);
                                setQuickTrimEnd(visibleDuration);
                                const priorTextStart = Number(priorRecipe?.textStartSec);
                                const priorTextEnd = Number(priorRecipe?.textEndSec);
                                const hasPriorTextTiming =
                                  hasPriorRecipe &&
                                  Number.isFinite(sourceStartSec) &&
                                  Number.isFinite(sourceEndSec) &&
                                  Number.isFinite(priorTextStart) &&
                                  Number.isFinite(priorTextEnd) &&
                                  priorTextStart >= sourceStartSec &&
                                  priorTextEnd > priorTextStart &&
                                  priorTextEnd <= sourceEndSec;
                                setQuickTextStart(
                                  hasPriorTextTiming ? priorTextStart - sourceStartSec : 0,
                                );
                                setQuickTextEnd(
                                  hasPriorTextTiming ? priorTextEnd - sourceStartSec : visibleDuration,
                                );
                                setQuickPlayhead(0);
                                setQuickIsPlaying(false);
                                setQuickAudio(
                                  hasPriorRecipe && priorRecipe?.audio === "mute"
                                    ? "mute"
                                    : hasPriorRecipe &&
                                        priorRecipe?.audio === "replace" &&
                                        typeof priorRecipe.audioAssetId === "string"
                                      ? "replace"
                                      : "keep",
                                );
                                setQuickMusicAssetId(
                                  hasPriorRecipe &&
                                    priorRecipe?.audio === "replace" &&
                                    typeof priorRecipe.audioAssetId === "string"
                                    ? priorRecipe.audioAssetId
                                    : "",
                                );
                              }}
                              className="h-7 px-2 text-xs"
                              data-testid={`button-edit-text-${run.id}`}
                            >
                              <Pencil className="mr-1 h-3.5 w-3.5" />
                              Quick edit
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant={isSaved ? "secondary" : "outline"}
                            onClick={() => saveRun.mutate(run.id)}
                            disabled={isSaved || savingThisRun}
                            className="h-7 px-2 text-xs"
                            data-testid={`button-save-run-${run.id}`}
                          >
                            {savingThisRun ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : isSaved ? (
                              <Check className="mr-1 h-3.5 w-3.5" />
                            ) : (
                              <Bookmark className="mr-1 h-3.5 w-3.5" />
                            )}
                            {savingThisRun ? "Saving" : isSaved ? "Saved" : "Save"}
                          </Button>
                          </>
                        ) : null}
                        {DELETABLE_STATUSES.has(run.status) && !run.quickEditInProgress ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteTarget(run)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${run.productName || "video"}`}
                            data-testid={`button-delete-${run.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
            ) : null}

            {!legacyRunsResponse && groupedRuns.recent.length > 0 ? (
              <Card className="p-4" data-testid="panel-video-reference-history">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 text-left"
                  onClick={() => setHistoryOpen((open) => !open)}
                  aria-expanded={historyOpen}
                  data-testid="button-toggle-video-reference-history"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <History className="h-4 w-4 text-primary" aria-hidden="true" />
                    Recent history
                    <Badge variant="secondary" className="text-[10px]" data-testid="badge-video-reference-history-count">
                      {groupedRuns.recent.length}
                    </Badge>
                  </span>
                  {historyOpen
                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                </button>
                {historyOpen ? (
                  <div className="mt-3 divide-y divide-border/60">
                    {groupedRuns.recent.map((group) => {
                      const expanded = expandedHistoryId === group.id;
                      const failed = group.counts.failed;
                      return (
                        <div key={group.id} data-testid={`video-reference-history-entry-${group.id}`}>
                          <div className="flex items-center gap-3 py-2.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{runGroupTitle(group)}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {formatWhen(group.createdAt)}
                                {" · "}
                                {group.counts.ready} ready
                                {failed ? ` · ${failed} failed` : ""}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-expanded={expanded}
                              onClick={() => {
                                if (expanded) {
                                  setExpandedHistoryId(null);
                                } else {
                                  setExpandedHistoryId(group.id);
                                }
                              }}
                              data-testid={`button-toggle-video-reference-history-${group.id}`}
                            >
                              {expanded ? "Hide results" : "View results"}
                            </Button>
                          </div>
                          {expanded ? (
                            <div
                              ref={setHistoryResultsTarget}
                              className="grid grid-cols-1 gap-4 pb-4 sm:grid-cols-2 lg:grid-cols-3"
                              data-testid={`video-reference-history-results-${group.id}`}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </Card>
            ) : null}
          </>
        )}
      </div>

      <Dialog
        open={phonePreview !== null}
        onOpenChange={(open) => {
          if (!open) setPhonePreview(null);
        }}
      >
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-[380px] p-5 sm:max-w-[380px]"
          data-testid="dialog-video-result-preview"
        >
          <DialogHeader className="pr-6">
            <DialogTitle>{phonePreview?.displayName || "Video preview"}</DialogTitle>
            <DialogDescription className="sr-only">
              Autoplaying video with playback controls
            </DialogDescription>
          </DialogHeader>
          {phonePreview?.videoUrl ? (
            <div className="mx-auto w-full max-w-[300px] min-w-0" data-testid="frame-phone">
              <div className="rounded-[2rem] border border-border bg-foreground/90 p-[6px] shadow-lg dark:bg-black">
                <div className="relative overflow-hidden rounded-[1.65rem] bg-black">
                  <div className="relative flex items-center justify-between bg-black px-4 pb-1 pt-1.5 text-[9px] font-medium text-white/80">
                    <span>9:41</span>
                    <span className="absolute left-1/2 top-1 h-3.5 w-16 -translate-x-1/2 rounded-full bg-white/10" />
                    <span className="flex items-center gap-1">
                      <span className="h-1.5 w-2.5 rounded-[2px] bg-white/70" />
                      <span className="h-1.5 w-3 rounded-[2px] bg-white/70" />
                    </span>
                  </div>
                  <video
                    key={phonePreview.videoUrl}
                    src={phonePreview.videoUrl}
                    className="aspect-[9/16] w-full bg-black object-contain"
                    autoPlay
                    controls
                    muted
                    playsInline
                    aria-label={`${phonePreview.displayName} video`}
                    data-testid="video-result-preview"
                  />
                  <div className="flex justify-center bg-black py-1.5">
                    <span className="h-1 w-20 rounded-full bg-white/40" />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={false}
        onOpenChange={(open) => {
          if (
            !open &&
            !regenerateText.isPending &&
            uploadingAssetKind === null
          ) {
            setTextEditTarget(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {typeof textEditTarget?.exactText === "string"
                ? "Edit and regenerate text"
                : "Add text to video"}
            </DialogTitle>
            <DialogDescription>
              This reuses the completed video and soundtrack. Only the deterministic text overlay is regenerated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="regenerate-exact-text">
                Exact on-video text
              </label>
              <Textarea
                id="regenerate-exact-text"
                value={textEditValue}
                onChange={(event) => setTextEditValue(event.target.value)}
                rows={4}
                maxLength={500}
                disabled={regenerateText.isPending}
                data-testid="input-regenerate-exact-text"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="regenerate-text-font">
                Font
              </label>
               <Select
                 value={pickerValueFor("text-edit", textEditFontChoice)}
                 onValueChange={(value) => {
                   void handleFontPickerChange(value, "text-edit");
                 }}
                 onOpenChange={(open) => {
                   if (open) {
                     ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((font) => font.family));
                   }
                 }}
                 disabled={
                   regenerateText.isPending ||
                   uploadingAssetKind !== null ||
                   referenceAssetsLoading ||
                   pendingGoogleFont !== null
                 }
               >
                 <SelectTrigger
                   id="regenerate-text-font"
                   className="h-9 w-full"
                   data-testid="select-regenerate-text-font"
                   data-value={pickerValueFor("text-edit", textEditFontChoice)}
                 >
                   <SelectValue placeholder="Default (Inter)" />
                 </SelectTrigger>
                 <SelectContent className="max-h-72">
                   {typeof textEditTarget?.exactText === "string" ? (
                     <SelectItem value={KEEP_CURRENT_FONT} data-value={KEEP_CURRENT_FONT}>
                       Keep current ({runFontLabel(textEditTarget)})
                     </SelectItem>
                   ) : null}
                   <SelectItem value={DEFAULT_FONT} data-value={DEFAULT_FONT}>
                     Default (Inter)
                   </SelectItem>
                   <SelectSeparator />
                   <VideoReferenceFontOptions fontAssets={fontAssets} />
                 </SelectContent>
               </Select>
              {pendingGoogleFont?.target === "text-edit" ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Preparing {pendingGoogleFont.family}…
                </p>
              ) : null}
              {referenceAssetsFetching && !referenceAssetsLoading ? (
                <p className="text-xs text-muted-foreground">
                  Refreshing the font library…
                </p>
              ) : null}
              {referenceAssetsError ? (
                <p className="text-xs text-destructive" role="alert">
                  {referenceAssetsError instanceof Error
                    ? referenceAssetsError.message
                    : "Could not load the font library"}
                </p>
              ) : null}
              <p
                className="rounded-md bg-muted/40 px-3 py-2 text-lg"
                style={{
                  fontFamily: textEditSpecimenAsset
                    ? `"video-ref-picker-${textEditSpecimenAsset.id}", sans-serif`
                    : textEditFontChoice === KEEP_CURRENT_FONT &&
                        textEditTarget?.textFontFamily
                      ? `"${textEditTarget.textFontFamily}", Inter, sans-serif`
                      : "Inter, sans-serif",
                }}
                data-testid="text-regenerate-font-specimen"
              >
                Aa Bb Cc 0123 — {textEditValue.trim() || "Your exact text"}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="regenerate-text-placement">
                Vertical placement
              </label>
              <select
                id="regenerate-text-placement"
                value={textEditPlacement}
                onChange={(event) =>
                  setTextEditPlacement(event.target.value as VideoReferenceTextPlacement)
                }
                disabled={regenerateText.isPending}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="select-regenerate-text-placement"
              >
                <option value="top">Top</option>
                <option value="upper-third">Upper third</option>
                <option value="middle">Middle</option>
                <option value="lower-third">Lower third</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium" htmlFor="regenerate-text-font-size">
                    Font size
                  </label>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {textEditFontSize}px
                  </span>
                </div>
                <input
                  id="regenerate-text-font-size"
                  type="range"
                  min={12}
                  max={240}
                  step={1}
                  value={textEditFontSize}
                  onChange={(event) => setTextEditFontSize(Number(event.target.value))}
                  disabled={regenerateText.isPending}
                  className="w-full accent-primary"
                  data-testid="input-regenerate-text-font-size"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm font-medium" htmlFor="regenerate-text-max-width">
                    Screen width
                  </label>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {textEditMaxWidthPercent}%
                  </span>
                </div>
                <input
                  id="regenerate-text-max-width"
                  type="range"
                  min={10}
                  max={100}
                  step={1}
                  value={textEditMaxWidthPercent}
                  onChange={(event) => setTextEditMaxWidthPercent(Number(event.target.value))}
                  disabled={regenerateText.isPending}
                  className="w-full accent-primary"
                  data-testid="input-regenerate-text-max-width"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setTextEditTarget(null)}
                disabled={regenerateText.isPending || uploadingAssetKind !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => regenerateText.mutate()}
                disabled={
                  !textEditValue.trim() ||
                  regenerateText.isPending ||
                  uploadingAssetKind !== null
                }
                data-testid="button-confirm-regenerate-text"
              >
                {regenerateText.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {typeof textEditTarget?.exactText === "string"
                  ? "Regenerate text"
                  : "Add text"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteRun.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this video?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the video and its generated files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRun.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (deleteTarget) deleteRun.mutate(deleteTarget.id);
              }}
              disabled={!deleteTarget || deleteRun.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-video"
            >
              {deleteRun.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</>
              ) : "Delete video"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function queueGroupSummary(group: VideoReferenceRunViewGroup<SanitizedRun>): string {
  if (group.kind === "single") {
    const run = group.runs[0];
    return `${statusMeta(run).label} — ${run.productName || "Product"} · ${
      run.personaCharacterName || "Persona model"
    }`;
  }
  const parts: string[] = [];
  if (group.counts.ready) parts.push(`${group.counts.ready} ready`);
  if (group.counts.active) parts.push(`${group.counts.active} rendering`);
  if (group.counts.queued) parts.push(`${group.counts.queued} queued`);
  if (group.counts.failed) parts.push(`${group.counts.failed} failed`);
  if (group.counts.otherTerminal) parts.push(`${group.counts.otherTerminal} finished`);
  return `${runGroupTitle(group)} — ${parts.join(", ")}`;
}

const googleFontOptionValue = (family: string) =>
  `${GOOGLE_FONT_OPTION_PREFIX}${family}`;

const GOOGLE_FONT_CATEGORIES = [
  "sans-serif",
  "serif",
  "display",
  "handwriting",
  "monospace",
] as const;

function VideoReferenceFontOptions({
  fontAssets,
}: {
  fontAssets: VideoReferenceAssetPublic[];
}) {
  return (
    <>
      {GOOGLE_FONT_CATEGORIES.map((category) => (
        <SelectGroup
          key={category}
          aria-label={category}
        >
          <SelectLabel className="-mx-1 mb-1 mt-1 px-3 py-1.5 bg-muted/80 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {category === "sans-serif"
              ? "Sans serif"
              : category.charAt(0).toUpperCase() + category.slice(1)}
          </SelectLabel>
          {GOOGLE_FONT_CATALOG
            .filter((font) => font.category === category)
            .map((font) => (
              <SelectItem
                key={font.family}
                value={googleFontOptionValue(font.family)}
                data-value={googleFontOptionValue(font.family)}
                style={{ fontFamily: `"${font.family}", sans-serif` }}
                data-testid={`option-video-reference-font-${font.family
                  .replace(/\s+/g, "-")
                  .toLowerCase()}`}
              >
                {font.family}
              </SelectItem>
            ))}
        </SelectGroup>
      ))}
      {fontAssets.length > 0 ? (
        <SelectGroup>
          <SelectLabel className="-mx-1 mb-1 mt-1 px-3 py-1.5 bg-primary/10 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Saved fonts
          </SelectLabel>
          {fontAssets.map((asset) => (
            <SelectItem key={asset.id} value={asset.id} data-value={asset.id}>
              {asset.label}
            </SelectItem>
          ))}
        </SelectGroup>
      ) : null}
    </>
  );
}

function isRunViewActive(run: SanitizedRun): boolean {
  return ACTIVE_STATUSES.has(run.status) || run.quickEditInProgress === true;
}

function updateRunsData(
  data: VideoReferenceRunsData | undefined,
  update: (run: SanitizedRun) => SanitizedRun | null,
): VideoReferenceRunsData | undefined {
  if (!data) return data;
  if (Array.isArray(data)) {
    return data.map(update).filter((run): run is SanitizedRun => run !== null);
  }
  const allRuns = [...data.active, ...data.recent]
    .flatMap((group) => group.runs)
    .map(update)
    .filter((run): run is SanitizedRun => run !== null);
  const regrouped = groupVideoReferenceRuns(allRuns, isRunViewActive);
  return {
    active: regrouped.active,
    recent: regrouped.recent.slice(0, VIDEO_REFERENCE_RECENT_GROUP_LIMIT),
  };
}

export function composeVideoReferenceStarterPrompt({
  products,
  personas,
  selectedPersona,
  readyCharacters,
  selectedPersonaCharactersLoaded,
  fontAssets,
  musicAssets,
  reference,
}: {
  products: Product[];
  personas: StarterPromptNamedItem[];
  selectedPersona?: StarterPromptNamedItem;
  readyCharacters: StarterPromptNamedItem[];
  selectedPersonaCharactersLoaded: boolean;
  fontAssets: StarterPromptAsset[];
  musicAssets: StarterPromptAsset[];
  reference: UploadedReference | null;
}): string {
  const list = (items: StarterPromptNamedItem[], empty: string) =>
    items.length > 0 ? items.map((item) => `- ${item.id}: ${item.name}`).join("\n") : empty;
  const assets = (items: StarterPromptAsset[], empty: string) =>
    items.length > 0 ? items.map((item) => `- ${item.id}: ${item.label}`).join("\n") : empty;
  const productLines = products.map((product) => {
    const variantNames = normalizeProductVariants(product.variantsJson)
      .map((variant) => variant.name.trim())
      .filter(Boolean);
    return `- ${product.id}: ${product.name}${variantNames.length > 0
      ? ` (catalog variants: ${variantNames.join(", ")})`
      : ""}`;
  });
  const modelContext = !selectedPersona
    ? "No persona is selected. Select a persona on the page to include its already-loaded ready model IDs; do not invent a characterId."
    : !selectedPersonaCharactersLoaded
      ? `Ready models for ${selectedPersona.name} (${selectedPersona.id}) are not loaded yet; do not invent a characterId.`
      : list(
          readyCharacters,
          `No ready models are available for ${selectedPersona.name} (${selectedPersona.id}); do not invent a characterId.`,
        );
  const referenceContext = !reference
    ? "No reference upload is selected; select a trusted upload before using referenceUploadId."
    : reference.uploadId
      ? `- ${reference.uploadId}: ${reference.fileName}; ${reference.durationSec}s; ${reference.width}x${reference.height}`
      : `${reference.fileName} is selected, but it has no trusted referenceUploadId; do not invent one.`;

  return [
    "Create a Video Reference Ads batch configuration for this brand.",
    "Return raw JSON only. Do not use Markdown fences, commentary, or identifiers that are not listed below.",
    "",
    `Uploader contract: ${VIDEO_REFERENCE_BATCH_UPLOADER_EXPLANATION}`,
    `Limits: 1–${VIDEO_REFERENCE_BATCH_MAX_ITEMS} items; variant and exactText are each at most ${VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS} characters.`,
    "",
    "Worked example:",
    JSON.stringify(VIDEO_REFERENCE_BATCH_WORKED_EXAMPLE, null, 2),
    "",
    "Available products:",
    productLines.length > 0 ? productLines.join("\n") : "No products are currently loaded; do not invent product IDs.",
    "",
    "Available personas:",
    list(personas, "No selectable personas are currently loaded; do not invent persona IDs."),
    "",
    "Ready models for the selected persona only:",
    modelContext,
    "",
    "Available font assets:",
    assets(fontAssets, "No font assets are currently loaded; omit fontAssetId."),
    "",
    "Available music assets:",
    assets(musicAssets, "No music assets are currently loaded; omit musicAssetId for automatic music."),
    "",
    "Selected reference upload:",
    referenceContext,
  ].join("\n");
}

type StarterPromptAsset = { id: string; label: string };
